import React, { useState } from 'react';
import { productExportWeight } from '../utils/productExportWeight';
import { koreaNumericValues } from '../utils/koreaLivestreamExport';
import { writeLegacyProductSafely as setDoc, deleteLegacyProductSafely as deleteDoc, mergeLegacyProductsSafely } from '../utils/productPersistence';
import * as XLSX from 'xlsx';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc, addDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import db from '../firebaseConfig';
import { functions } from '../firebaseConfig';
import { combineBarcode, extractRawBarcode } from '../utils/barcodeUtils';
import { assertLegacyImportPreflight, createLegacyImportIndex, inspectLegacyImportRow, legacyImportRowNumber, readLegacyImportIdentity } from '../utils/legacyImportPreflight';
import {
  chooseMainProductForMerge,
  fillMissingProductFields,
  hasConflictingItemNumbers,
} from '../utils/productMerge';
import { buildProductPhotos, parseAlbumLinks } from '../utils/productPhotos';
import { cleanProductData } from '../utils/productSnapshotPersistence';
import { getCollectionName, isSimpleProductCountry } from '../utils/countryConfig';
import { handleError, handleSuccess, handleWarning, handleInfo } from '../utils/errorHandler';
import { useToast } from './Toast';
import '../styles.css';

const buildPhotosFromImport = ({ existingPhotos, albumValue, productImage }) => buildProductPhotos({
  image: productImage || undefined,
  photos: [...(existingPhotos || []), ...parseAlbumLinks(albumValue)],
});

// 解析子商品或贈品字串
// 格式：KA1652A(--)*1,KA1653(--)*1 或 【滿5件送】KB566(--)*1,KA1653(--)*1
// 或：【滿6件送1(任選)】KA126(石榴/蘋果/荔枝)*1（多樣式任選）
// 或：[由高至低]【滿6件送1(任選)】KA126(石榴/蘋果/荔枝)*1（多層贈品）
const parseSubProductsOrGifts = (str) => {
  if (!str || str.trim() === '' || str.trim() === '--') {
    return [];
  }
  
  const items = [];
  const trimmedStr = str.trim();
  
  // 檢查是否有模式標識（多層贈品）
  // 模式標識可能在第一行，格式：[由高至低]、[只取最高]、[各自計算]
  let mode = null; // '由高至低' | '只取最高' | '各自計算'
  let contentStr = trimmedStr;
  
  // 先檢查第一行是否有模式標識
  const lines = trimmedStr.split(/\r?\n/).map(l => l.trim()).filter(l => l);
  if (lines.length > 0) {
    const firstLine = lines[0];
    const modeMatch = firstLine.match(/^\[(由高至低|只取最高|各自計算)\](.*)$/);
    if (modeMatch) {
      mode = modeMatch[1];
      // 如果第一行還有內容，保留它；否則從第二行開始
      const firstLineContent = modeMatch[2].trim();
      if (firstLineContent) {
        contentStr = [firstLineContent, ...lines.slice(1)].join(',');
      } else {
        contentStr = lines.slice(1).join(',');
      }
    } else {
      // 沒有模式標識，使用所有行
      contentStr = lines.join(',');
    }
  }
  
  // 使用逗號或換行符號分割
  const parts = contentStr.split(/[,\n\r]+/).map(p => p.trim()).filter(p => p && p !== '--');
  
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed || trimmed === '--') continue;
    
    // 解析格式：料號(樣式)*數量 或 【標題】料號(樣式)*數量
    // 例如：KA1652A(--)*1 或 【滿5件送】KB566(--)*1
    // 或：【滿6件送1(任選)】KA126(石榴/蘋果/荔枝)*1（多樣式任選）
    
    // 先檢查是否有標題（【】）
    let title = '';
    let content = trimmed;
    const titleMatch = trimmed.match(/^【([^】]+)】(.+)$/);
    if (titleMatch) {
      title = titleMatch[1];
      content = titleMatch[2];
    }
    
    // 解析料號(樣式)*數量
    // 格式：料號(樣式)*數量 或 料號*數量
    // 樣式可以是單一樣式或多樣式（用斜線分隔）：樣式1/樣式2/樣式3
    const match = content.match(/^([^(]+)(?:\(([^)]*)\))?\*(\d+)$/);
    if (match) {
      const itemNumber = match[1].trim();
      const styleStr = match[2] ? match[2].trim() : '';
      const quantity = parseInt(match[3], 10);
      
      // 檢查是否為多樣式（用斜線分隔）
      if (styleStr.includes('/')) {
        // 多樣式任選：將每個樣式拆分為單獨的項目
        const styles = styleStr.split('/').map(s => s.trim()).filter(s => s);
        styles.forEach(style => {
          items.push({
            title: title,
            itemNumber: itemNumber,
            style: style,
            quantity: quantity || 1,
            isMultiStyle: true, // 標記為多樣式
            mode: mode // 多層贈品模式
          });
        });
      } else {
        // 單一樣式
        items.push({
          title: title,
          itemNumber: itemNumber,
          style: styleStr || '',
          quantity: quantity || 1,
          isMultiStyle: false,
          mode: mode // 多層贈品模式
        });
      }
    } else {
      // 如果格式不符合，嘗試解析為簡單格式：料號*數量
      const simpleMatch = content.match(/^(.+)\*(\d+)$/);
      if (simpleMatch) {
        const itemNumber = simpleMatch[1].trim();
        const quantity = parseInt(simpleMatch[2], 10);
        items.push({
          title: title,
          itemNumber: itemNumber,
          style: '',
          quantity: quantity || 1,
          isMultiStyle: false,
          mode: mode // 多層贈品模式
        });
      }
    }
  }
  
  return items;
};

// 清理資料，移除所有 undefined 值，確保 Firestore 兼容
const cleanDataForFirestore = cleanProductData;


function BatchAddProduct() {
  const { country } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const collectionName = getCollectionName(country);
  const [file, setFile] = useState(null);
  const [exportStatus, setExportStatus] = useState('');
  const [sameItemNumberGroups, setSameItemNumberGroups] = useState([]); // 相同料號的商品組
  // 使用普通對象代替 Set 以避免初始化問題
  const [selectedProducts, setSelectedProducts] = useState({}); // 選中的商品ID { productId: true }
  const [showMergeSelection, setShowMergeSelection] = useState(false); // 是否顯示選擇界面
  const [uploadProgress, setUploadProgress] = useState(null); // 上傳進度 { current: 0, total: 0, status: 'idle' }
  const [uploadErrors, setUploadErrors] = useState([]); // 上傳錯誤列表
  const [isUploading, setIsUploading] = useState(false); // 是否正在上傳
  const [isRebuildingIndex, setIsRebuildingIndex] = useState(false);
  const [indexRebuildStatus, setIndexRebuildStatus] = useState('');
  const [isRebuildingImageIndex, setIsRebuildingImageIndex] = useState(false);
  const [imageIndexRebuildStatus, setImageIndexRebuildStatus] = useState('');
  const [mergePreview, setMergePreview] = useState(null); // 合併預覽 { groups: [], type: 'auto' | 'manual' }
  const [abnormalProducts, setAbnormalProducts] = useState(null); // 異常商品列表 { products: [], show: boolean }
  const [refreshKey, setRefreshKey] = useState(0); // 用於觸發重新載入數據
  const [showRemoveStyles, setShowRemoveStyles] = useState(false); // 是否顯示移除樣式界面
  const [itemNumbersInput, setItemNumbersInput] = useState(''); // 料號輸入
  const [productsWithStyles, setProductsWithStyles] = useState([]); // 查詢到的商品及其樣式
  // 使用普通對象代替 Map 以避免初始化問題
  const [selectedStylesToRemove, setSelectedStylesToRemove] = useState({}); // 選中要移除的樣式 { 'productId-styleIndex': true }
  const [removeAllVerificationCode, setRemoveAllVerificationCode] = useState(''); // 全部移除驗證碼
  const [removeAllInputCode, setRemoveAllInputCode] = useState(''); // 全部移除輸入的驗證碼
  const [barcodeKeyword, setBarcodeKeyword] = useState(''); // 條碼關鍵字輸入

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileUpload = async () => {
    if (!file) {
      handleWarning('請選擇檔案');
      return;
    }

    setIsUploading(true);
    setUploadErrors([]);
    setUploadProgress({ current: 0, total: 0, status: 'reading' });

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(worksheet).map(row => ({
        ...row,
        ...(row.圖片網址 !== undefined ? { 圖片: row.圖片網址 } : {}),
        ...(row.樣式圖片網址 !== undefined ? { 樣式圖片: row.樣式圖片網址 } : {}),
      }));
        
        if (!json || json.length === 0) {
          handleWarning('檔案中沒有資料');
          setIsUploading(false);
          setUploadProgress(null);
          return;
        }

      const productsRef = collection(db, collectionName);
        const total = json.length;
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        setUploadProgress({ current: 0, total, status: 'preflighting' });

        // Inspect the whole imported sheet before ANY vendor or product write.
        const allProductsSnapshot = await getDocs(productsRef);
        const legacyProductIndex = createLegacyImportIndex(allProductsSnapshot.docs.map(snapshot => ({ ...snapshot.data(), id: snapshot.id })));
        assertLegacyImportPreflight(json, legacyProductIndex);
        const findCurrentImportProduct = async (item, originalIndex) => {
          const matches = legacyProductIndex.find(item);
          const issue = inspectLegacyImportRow(item, originalIndex, matches);
          if (issue) throw new Error(issue);
          if (!matches.length) return null;
          // Refresh for every row, including repeated SKUs, to use the latest
          // revision and retain all styles already written earlier in this file.
          const snapshot = await getDoc(doc(productsRef, matches[0].id));
          if (!snapshot.exists()) throw new Error('商品已被刪除，請重新載入並檢查檔案');
          const current = { ...snapshot.data(), id: snapshot.id };
          const currentIssue = inspectLegacyImportRow(item, originalIndex, [current]);
          if (currentIssue) throw new Error(currentIssue);
          legacyProductIndex.upsert(current);
          if (!legacyProductIndex.find(item).some(product => product.id === current.id)) {
            throw new Error('商品料號／條碼已變更，請重新載入並檢查檔案');
          }
          return snapshot;
        };
        const persistImportedProduct = async (ref, product, base) => {
          const saved = await setDoc(ref, product, base);
          legacyProductIndex.upsert({ ...saved, id: ref.id });
          return saved;
        };
        setUploadProgress({ current: 0, total, status: 'uploading' });

        // 預先載入所有現有商品和廠商資料（優化查詢）
        const vendorsRef = collection(db, 'vendors');
        const allVendorsSnapshot = await getDocs(query(vendorsRef, where('country', '==', country)));
        // 使用普通對象代替 Set 以避免初始化問題
        const existingVendors = {};
        allVendorsSnapshot.forEach(doc => {
          existingVendors[doc.data().name] = true;
        });

        // 先將相同料號的商品分組，確保相同料號的商品按順序處理（避免並行處理時的競態條件）
        // 用 Map 保留所有字面料號（包含 __proto__ 等字串）。
        const itemsByItemNumber = new Map();
        json.forEach((item, index) => {
          const { itemNumber } = readLegacyImportIdentity(item);
          if (!itemsByItemNumber.has(itemNumber)) itemsByItemNumber.set(itemNumber, []);
          itemsByItemNumber.get(itemNumber).push({ item, originalIndex: index });
        });
        
        // 將分組後的商品重新排列：相同料號的商品連續排列
        const sortedItems = [];
        itemsByItemNumber.forEach(items => sortedItems.push(...items));
        
        // 批次處理商品（每10個一組，避免過度負載）
        // 注意：相同料號的商品現在會連續處理，避免競態條件
        const batchSize = 10;
        for (let i = 0; i < sortedItems.length; i += batchSize) {
          const calculatedEndIndex = i + batchSize;
          const endIndex = calculatedEndIndex < sortedItems.length ? calculatedEndIndex : sortedItems.length;
          const batch = sortedItems.slice(i, endIndex);
          
          // 對於相同料號的商品，需要按順序處理（不能並行）
          // 先找出哪些商品有相同的料號
          const itemNumbersInBatch = new Set();
          batch.forEach(({ item }) => {
            const { itemNumber } = readLegacyImportIdentity(item);
            itemNumbersInBatch.add(itemNumber);
          });
          
          // 如果批次中有相同料號的商品，需要按順序處理
          const uniqueItemNumbersCount = itemNumbersInBatch.size;
          if (uniqueItemNumbersCount < batch.length) {
            // 有重複料號，按順序處理
            for (const { item, originalIndex } of batch) {
              const globalIndex = originalIndex;
              try {
        const { itemNumber } = readLegacyImportIdentity(item);
        const style = item.樣式 ? item.樣式 : '無樣式';
              const { rawStyleBarcode } = readLegacyImportIdentity(item); // 原始條碼
              let styleBarcode = rawStyleBarcode ? combineBarcode(rawStyleBarcode, itemNumber) : ''; // 組合條碼（條碼+料號），如果沒有條碼則為空
              // 樣式圖片（可選）- 確保正確讀取和處理
              // 驗證樣式圖片是否為有效的 URL，如果包含贈品格式（【】）則視為無效
              let styleImage = '';
              if (item.樣式圖片 !== undefined && item.樣式圖片 !== null) {
                const rawStyleImage = String(item.樣式圖片).trim();
                // 檢查是否包含贈品格式標記，如果包含則視為無效圖片 URL
                if (rawStyleImage && !rawStyleImage.includes('【') && !rawStyleImage.includes('】')) {
                  // 簡單驗證是否為 URL 格式（以 http:// 或 https:// 開頭）
                  if (rawStyleImage.startsWith('http://') || rawStyleImage.startsWith('https://')) {
                    styleImage = rawStyleImage;
                  }
                }
              }
              // 樣式級別的價格：直接使用直播價、商成價作為樣式價格（Excel一行就是一個樣式）
              const styleLivePrice = String(item.直播價 || '').trim();
              const styleOnlinePrice = String(item.商成價 || '').trim();
              // 使用原本的成本和重量欄位作為樣式成本和重量（Excel一行就是一個樣式）
              const styleCostKoreanWon = String(item.成本韓幣 || '').trim(); // 樣式成本韓幣
              const styleCostJPY = String(item.成本日幣 || '').trim(); // 樣式成本日幣
              const styleCostTWD = String(item.成本台幣 || '').trim(); // 樣式成本台幣
              const styleCostTHB = String(item.成本泰銖 || '').trim(); // 樣式成本泰銖
              const styleWeight = String(item.重量 || '').trim(); // 樣式重量
        const vendorName = String(item.廠商 || '').trim();
              
              // 處理主要商品圖片（單一圖片）
              const productImage = String(item.圖片 || '').trim();
              
              // 處理子商品欄位
              const subProductsStr = String(item.子商品 || '').trim();
              const subProducts = parseSubProductsOrGifts(subProductsStr);
              
              // 處理贈品模式欄位（多層贈品）
              // 如果 Excel 中有「贈品模式」欄位，使用該值；否則從贈品內容中解析
              let giftMode = null; // '由高至低' | '只取最高' | '各自計算'
              if (item.贈品模式 !== undefined && item.贈品模式 !== null) {
                const modeStr = String(item.贈品模式).trim();
                if (modeStr === '由高至低' || modeStr === '只取最高' || modeStr === '各自計算') {
                  giftMode = modeStr;
                }
              }
              
              // 處理贈品欄位
              // 如果 Excel 中沒有「贈品」欄位（undefined），則保留現有值（設為 undefined）
              // 如果 Excel 中有「贈品」欄位但為空字符串，則保留現有值（設為 undefined）
              // 如果 Excel 中「贈品」欄位是 '--' 或 '—'，則刪除贈品（設為 null）
              // 如果 Excel 中「贈品」欄位有值，則更新贈品
              let gifts = undefined; // undefined 表示保留現有值
              if (item.贈品 !== undefined && item.贈品 !== null) {
                const giftsStr = String(item.贈品).trim();
                if (giftsStr === '--' || giftsStr === '—') {
                  gifts = null; // null 表示刪除贈品
                } else if (giftsStr) {
                  // 如果已經有贈品模式，直接使用；否則從贈品內容中解析
                  const parsedGifts = parseSubProductsOrGifts(giftsStr);
                  // 如果從 Excel 中指定了贈品模式，覆蓋解析出的模式
                  if (giftMode) {
                    parsedGifts.forEach(gift => {
                      gift.mode = giftMode;
                    });
                  }
                  gifts = parsedGifts; // 有值則更新
                }
                // 如果 giftsStr 為空字符串，gifts 保持為 undefined（保留現有值）
              }

              const matchedImportProduct = await findCurrentImportProduct(item, originalIndex);
              if (matchedImportProduct && rawStyleBarcode && !itemNumber) {
                styleBarcode = combineBarcode(rawStyleBarcode, matchedImportProduct.data().itemNumber || '');
              }

              // 處理廠商（如果不存在則新增）
              if (vendorName !== '' && !existingVendors[vendorName]) {
                try {
            await addDoc(vendorsRef, { name: vendorName, country });
                  existingVendors[vendorName] = true;
                } catch (vendorError) {
                  console.warn(`新增廠商失敗: ${vendorName}`, vendorError);
                  // 不中斷流程，繼續處理商品
          }
        }

              // 查詢現有商品
        let existingProduct = matchedImportProduct;
              try {
                // 先查組合條碼（條碼+料號）- 最精確的匹配
                if (!existingProduct && styleBarcode) {
                  const existingProductByBarcode = await getDocs(
                    query(productsRef, where('styleBarcodes', 'array-contains', styleBarcode))
                  );
        if (!existingProductByBarcode.empty) {
                    // 驗證商品確實存在（檢查是否有基本字段）
                    const candidate = existingProductByBarcode.docs[0];
                    const candidateData = candidate.data();
                    // 確保商品有基本資料（名稱或料號），避免找到已刪除的殘留資料
                    if (candidateData && (candidateData.name || candidateData.itemNumber)) {
                      existingProduct = candidate;
                    }
                  }
                }
                
                // 如果沒找到，再查原始條碼（但必須料號也匹配，避免錯誤合併）
                if (!existingProduct && rawStyleBarcode) {
                  const existingProductsByRawBarcode = await getDocs(
                    query(productsRef, where('styleBarcodes', 'array-contains', rawStyleBarcode))
                  );
                  
                  if (!existingProductsByRawBarcode.empty) {
                    // 檢查是否有料號匹配的商品
                    // 優先找有 rawStyleBarcodes 的商品（已遷移）
                    let matchedProduct = existingProductsByRawBarcode.docs.find(doc => {
                      const data = doc.data();
                      // 確保商品有基本資料，避免找到已刪除的殘留資料
                      if (!data || (!data.name && !data.itemNumber)) {
                        return false;
                      }
                      // 如果商品有 rawStyleBarcodes，檢查 rawStyleBarcodes 是否包含原始條碼
                      if (data.rawStyleBarcodes && data.rawStyleBarcodes.length > 0) {
                        return data.rawStyleBarcodes.includes(rawStyleBarcode) && 
                               data.itemNumber === itemNumber;
                      }
                      // 如果商品沒有 rawStyleBarcodes（未遷移），必須料號匹配才更新
                      return data.itemNumber === itemNumber;
                    });
                    
                    // 如果沒找到料號匹配的，但有料號，就不更新（避免錯誤合併）
                    // 如果沒有料號，才使用第一個找到的商品（兼容沒有料號的情況）
                    if (matchedProduct) {
                      existingProduct = matchedProduct;
                    } else if (!itemNumber) {
                      // 沒有料號的情況，使用第一個找到的商品（但需要驗證商品存在）
                      const firstCandidate = existingProductsByRawBarcode.docs[0];
                      const firstData = firstCandidate.data();
                      if (firstData && (firstData.name || firstData.itemNumber)) {
                        existingProduct = firstCandidate;
                      }
                    }
                    // 如果有料號但沒匹配到，就不更新（避免錯誤合併）
                  }
                }
                
                // 如果沒找到，再查料號（只根據料號匹配）
                if (!existingProduct && itemNumber) {
                  const existingProductByItemNumber = await getDocs(
                    query(productsRef, where('itemNumber', '==', itemNumber))
                  );
                  if (!existingProductByItemNumber.empty) {
                    // 驗證商品確實存在（檢查是否有基本字段）
                    const candidate = existingProductByItemNumber.docs[0];
                    const candidateData = candidate.data();
                    if (candidateData && (candidateData.name || candidateData.itemNumber)) {
                      existingProduct = candidate;
                    }
                  }
                }
              } catch (queryError) {
                throw new Error(`查詢商品失敗: ${queryError.message}`);
              }

        if (existingProduct) {
                // 更新現有商品（使用原本的邏輯：item.圖片 || existingData.image）
          const existingData = existingProduct.data();
                const isComboProductUpdate = subProducts.length > 0 || (existingData.subProducts && existingData.subProducts.length > 0);
                
                // 組合商品不會有樣式也不會有條碼
                let updatedStyles = [];
                let updatedStyleBarcodes = [];
                let updatedRawStyleBarcodes = [];
                let updatedStyleDetails = [];
                let styleHistoryEntry = null;
                
                if (!isComboProductUpdate) {
                  // 一般商品：處理樣式和條碼
                  updatedStyles = existingData.styles ? [...existingData.styles] : [];
                  updatedStyleBarcodes = existingData.styleBarcodes ? [...existingData.styleBarcodes] : [];
                  updatedRawStyleBarcodes = existingData.rawStyleBarcodes ? [...existingData.rawStyleBarcodes] : [];
                  updatedStyleDetails = existingData.styleDetails ? [...existingData.styleDetails] : [];
                  
                  // 更新或新增樣式
                  // 如果沒有條碼，嘗試根據樣式名稱匹配，或使用第一個樣式
                  let styleIndex = -1;
                  
                  if (styleBarcode) {
                    // 有條碼，使用條碼匹配
                    styleIndex = updatedStyleBarcodes.indexOf(styleBarcode);
                  } else if (style && style !== '無樣式') {
                    // 沒有條碼，但有樣式名稱，根據樣式名稱匹配
                    styleIndex = updatedStyles.findIndex(s => s === style);
                  } else if (updatedStyles.length === 1) {
                    // 沒有條碼和樣式名稱，但商品只有一個樣式，使用第一個樣式
                    styleIndex = 0;
                  } else if (updatedStyles.length > 1) {
                    // 沒有條碼和樣式名稱，但商品有多個樣式，使用第一個樣式（更新商品級別資訊）
                    styleIndex = 0;
                  }
                  
                  // 更新或新增樣式，並記錄歷史變更
                  if (styleIndex >= 0) {
                  // 更新現有樣式，記錄變更前的狀態
                  const existingStyleDetail = updatedStyleDetails[styleIndex] || {};
                  // 歷史記錄不包含圖片（圖片變更不顯示在歷史價格中）
                  const beforeState = {
                    livePrice: existingStyleDetail.livePrice || null,
                    onlinePrice: existingStyleDetail.onlinePrice || null,
                    costKoreanWon: existingStyleDetail.costKoreanWon || null,
                    costJPY: existingStyleDetail.costJPY || null,
                    costTWD: existingStyleDetail.costTWD || null,
                    costTHB: existingStyleDetail.costTHB || null,
                    weight: existingStyleDetail.weight || null
                  };
                  
                  // 樣式圖片：如果 Excel 中有值則更新，如果為空字符串也更新為 null（清空），否則保留現有值
                  const rawStyleImageValue = item.樣式圖片;
                  // 檢查 Excel 中是否有「樣式圖片」欄位（undefined 或 null 表示沒有這個欄位）
                  const hasStyleImageColumn = rawStyleImageValue !== undefined && rawStyleImageValue !== null;
                  // 驗證樣式圖片是否為有效的 URL，如果包含贈品格式（【】）則視為無效
                  let validStyleImage = styleImage;
                  if (hasStyleImageColumn && styleImage) {
                    const trimmedImage = String(styleImage).trim();
                    // 檢查是否包含贈品格式標記，如果包含則視為無效圖片 URL
                    if (trimmedImage.includes('【') || trimmedImage.includes('】')) {
                      validStyleImage = null; // 無效的圖片 URL，設為 null
                    } else if (!trimmedImage.startsWith('http://') && !trimmedImage.startsWith('https://')) {
                      validStyleImage = null; // 不是有效的 URL，設為 null
                    }
                  }
                  // 如果有欄位，使用驗證後的值（trim 後如果為空則設為 null）；如果沒有欄位，保留現有值
                  const afterImage = hasStyleImageColumn ? (validStyleImage || null) : (existingStyleDetail.image || null);
                  
                  const afterState = {
                    livePrice: styleLivePrice ? styleLivePrice : (existingStyleDetail.livePrice || null),
                    onlinePrice: styleOnlinePrice ? styleOnlinePrice : (existingStyleDetail.onlinePrice || null),
                    costKoreanWon: styleCostKoreanWon ? styleCostKoreanWon : (existingStyleDetail.costKoreanWon || null),
                    costJPY: styleCostJPY ? styleCostJPY : (existingStyleDetail.costJPY || null),
                    costTWD: styleCostTWD ? styleCostTWD : (existingStyleDetail.costTWD || null),
                    costTHB: styleCostTHB ? styleCostTHB : (existingStyleDetail.costTHB || null),
                    weight: styleWeight ? styleWeight : (existingStyleDetail.weight || null)
                  };
                  
                  // 檢查是否有實際變更（不包含圖片）
                  const hasChanges = JSON.stringify(beforeState) !== JSON.stringify(afterState);
                  if (hasChanges) {
                    styleHistoryEntry = {
                      date: new Date().toLocaleDateString(),
                      style: style,
                      styleIndex: styleIndex,
                      before: beforeState,
                      after: afterState
                    };
                  }
                  
                  // 更新樣式詳細信息（包含圖片）
                  const updatedStyleDetail = {
                    image: afterImage,
                    ...afterState
                  };
                  
                    // 更新現有樣式
                    updatedStyles[styleIndex] = style;
                    // 如果有條碼，更新條碼；如果沒有條碼，保留現有條碼
                    if (rawStyleBarcode) {
                      updatedRawStyleBarcodes[styleIndex] = rawStyleBarcode;
                      updatedStyleBarcodes[styleIndex] = styleBarcode;
                    }
                    updatedStyleDetails[styleIndex] = updatedStyleDetail;
                  } else {
                    // 新增樣式（只有在有條碼或樣式名稱時才新增）
                    if (styleBarcode || (style && style !== '無樣式')) {
                      const styleDetail = {
                        image: styleImage || null,
                        livePrice: styleLivePrice || null,
                        onlinePrice: styleOnlinePrice || null,
                        costKoreanWon: styleCostKoreanWon || null,
                        costJPY: styleCostJPY || null,
                        costTWD: styleCostTWD || null,
                        costTHB: styleCostTHB || null,
                        weight: styleWeight || null
                      };
                      
                      // 歷史記錄不包含圖片
                      const styleDetailForHistory = {
                        livePrice: styleLivePrice || null,
                        onlinePrice: styleOnlinePrice || null,
                        costKoreanWon: styleCostKoreanWon || null,
                        costJPY: styleCostJPY || null,
                        costTWD: styleCostTWD || null,
                        costTHB: styleCostTHB || null,
                        weight: styleWeight || null
                      };
                      
                      styleHistoryEntry = {
                        date: new Date().toLocaleDateString(),
                        style: style,
                        styleIndex: updatedStyles.length,
                        before: null,
                        after: styleDetailForHistory
                      };
                      
                      updatedStyles.push(style);
                      updatedStyleBarcodes.push(styleBarcode || '');
                      updatedRawStyleBarcodes.push(rawStyleBarcode || '');
                      updatedStyleDetails.push(styleDetail);
                    }
                    // 如果沒有條碼也沒有樣式名稱，就不新增樣式，只更新商品級別資訊
                  }
                }
                // 組合商品不會有樣式也不會有條碼，所以不需要處理樣式相關邏輯

                // Excel一行就是一個樣式，成本和重量欄位作為樣式成本和重量
                // 商品級別的成本和重量保留原本的值（不更新）
                // 檢查是否需要自動開啟多圖片、多價格、多成本/重量開關（僅一般商品）
                const hasStyleImages = !isComboProductUpdate && updatedStyleDetails.some(detail => detail && detail.image);
                const hasStylePrices = !isComboProductUpdate && updatedStyleDetails.some(detail => detail && (detail.livePrice || detail.onlinePrice));
                const hasStyleCosts = !isComboProductUpdate && updatedStyleDetails.some(detail => detail && (detail.costKoreanWon || detail.costJPY || detail.costTWD || detail.costTHB || detail.weight));
                
                // 處理子商品：組合商品保留原始料號，但不修改品名（標籤在顯示時添加）
                let finalName = item.名稱 || existingData.name;
                let finalItemNumber = itemNumber || existingData.itemNumber || '';
                
                if (isComboProductUpdate) {
                  // 組合商品保留自己的料號，不設為「組合商品」
                  // 品名不再添加前綴，標籤在顯示時添加
                }
                
                // 處理贈品：
                // gifts === undefined：保留現有值（Excel 中沒有贈品欄位或為空）
                // gifts === null：刪除贈品（Excel 中贈品欄位是 '--' 或 '—'）
                // gifts 有值：更新贈品（Excel 中贈品欄位有內容）
                let finalGifts = existingData.gifts; // 預設保留現有值
                let giftHistoryEntry = null;
                if (gifts === null) {
                  // gifts === null 表示要刪除贈品
                  if (existingData.gifts && existingData.gifts.length > 0) {
                    giftHistoryEntry = {
                      date: new Date().toLocaleDateString(),
                      action: '移除贈品',
                      before: existingData.gifts,
                      after: null
                    };
                  }
                  finalGifts = null;
                } else if (gifts !== undefined && gifts && gifts.length > 0) {
                  // 有新的贈品資訊
                  const originalGiftsStr = JSON.stringify(existingData.gifts || []);
                  const newGiftsStr = JSON.stringify(gifts);
                  if (originalGiftsStr !== newGiftsStr) {
                    giftHistoryEntry = {
                      date: new Date().toLocaleDateString(),
                      action: '更新贈品',
                      before: existingData.gifts || null,
                      after: gifts
                    };
                  }
                  finalGifts = gifts;
                }
                // 如果 gifts === undefined，finalGifts 保持為 existingData.gifts（保留現有值）
                
                // 處理子商品變更記錄
                let subProductHistoryEntry = null;
                const originalSubProductsStr = JSON.stringify(existingData.subProducts || []);
                const newSubProductsStr = JSON.stringify(subProducts);
                if (originalSubProductsStr !== newSubProductsStr) {
                  subProductHistoryEntry = {
                    date: new Date().toLocaleDateString(),
                    action: subProducts.length > 0 ? '更新子商品' : '移除子商品',
                    before: existingData.subProducts || null,
                    after: subProducts.length > 0 ? subProducts : null
                  };
                }
                
                // 記錄商品級別的歷史（保留原有邏輯）
                const historyEntry = {
                  date: new Date().toLocaleDateString(),
                  costKoreanWon: existingData.costKoreanWon,
                  costJPY: existingData.costJPY,
                  costTWD: existingData.costTWD,
                  costTHB: existingData.costTHB,
                  livePrice: existingData.livePrice,
                  onlinePrice: existingData.onlinePrice,
                  remarks: existingData.remarks
                };
                
                // 組合歷史記錄
                const newHistory = [...(existingData.history || []), historyEntry];
                if (styleHistoryEntry) {
                  newHistory.push(styleHistoryEntry);
                }
                if (giftHistoryEntry) {
                  newHistory.push(giftHistoryEntry);
                }
                if (subProductHistoryEntry) {
                  newHistory.push(subProductHistoryEntry);
                }
                
                // 組合商品不會有樣式也不會有條碼
                const updatedData = {
                  ...existingData,
                  history: newHistory,
                  // 處理主要商品圖片
                  image: productImage || existingData.image || '',
                  photos: buildPhotosFromImport({
                    existingPhotos: existingData.photos,
                    albumValue: item.商品相簿,
                    productImage: productImage || existingData.image,
                  }),
                  name: finalName,
                  itemNumber: finalItemNumber,
                  // 商品級別的價格：保留現有值（因為Excel一行就是一個樣式，價格在樣式詳細信息中）
                  livePrice: existingData.livePrice || '',
                  onlinePrice: existingData.onlinePrice || '',
                  remarks: item.備註 === '--' ? '' : (item.備註 || existingData.remarks),
                  // 商品級別的成本和重量不更新（因為Excel一行就是一個樣式）
                  // 組合商品不會有樣式也不會有條碼
                  ...(isComboProductUpdate ? {
                    styles: [],
                    styleBarcodes: [],
                    rawStyleBarcodes: []
                  } : {
                    styles: updatedStyles,
                    styleBarcodes: updatedStyleBarcodes, // 組合條碼（條碼+料號）
                    rawStyleBarcodes: updatedRawStyleBarcodes, // 原始條碼
                    // 只有在有樣式詳細信息時才設置，否則不設置（讓 cleanDataForFirestore 移除）
                    styleDetails: updatedStyleDetails
                  }),
                  vendor: vendorName || existingData.vendor || '',
                  // 自動開啟對應的開關標記（用於編輯頁面自動開啟）
                  enableMultiImage: hasStyleImages || existingData.enableMultiImage || false,
                  enableMultiPrice: hasStylePrices || existingData.enableMultiPrice || false,
                  enableMultiCostWeight: hasStyleCosts || existingData.enableMultiCostWeight || false,
                  // 子商品和贈品
                  ...(subProducts.length > 0 ? { subProducts: subProducts } : {}),
                  ...(finalGifts !== undefined ? { gifts: finalGifts } : {})
                };
                if (!existingData.itemNumber && itemNumber) {
                  updatedData.itemNumber = itemNumber;
                }
                
                // 清理資料，移除所有 undefined 值（確保 Firestore 兼容）
                const cleanedData = cleanDataForFirestore(updatedData);
                
                try {
                  await persistImportedProduct(doc(productsRef, existingProduct.id), cleanedData, existingData);
                  successCount++;
                } catch (updateError) {
                  throw new Error(`更新商品失敗: ${updateError.message}`);
                }
              } else {
                // 新增商品前，先檢查是否有相同料號的商品（自動合併）
                // 注意：組合商品（有子商品）不可被合併
                let productToMerge = null;
                const isComboProduct = subProducts.length > 0;
                if (itemNumber && !isComboProduct) {
                  try {
                    const existingProductByItemNumber = await getDocs(
                      query(productsRef, where('itemNumber', '==', itemNumber))
                    );
                    if (!existingProductByItemNumber.empty) {
                      const candidate = existingProductByItemNumber.docs[0];
                      const candidateData = candidate.data();
                      // 組合商品不可被合併，也不可合併其他商品（根據是否有子商品判斷）
                      if (candidateData && (candidateData.name || candidateData.itemNumber) && 
                          !candidateData.subProducts) {
                        productToMerge = candidate;
                      }
                    }
                  } catch (mergeQueryError) {
                    console.warn(`查詢相同料號商品失敗: ${mergeQueryError.message}`);
                  }
                }
                
                if (productToMerge) {
                  // 合併到現有商品
                  const existingData = productToMerge.data();
                  const updatedStyles = existingData.styles ? [...existingData.styles] : [];
                  const updatedStyleBarcodes = existingData.styleBarcodes ? [...existingData.styleBarcodes] : [];
                  const updatedRawStyleBarcodes = existingData.rawStyleBarcodes ? [...existingData.rawStyleBarcodes] : [];
                  const updatedStyleDetails = existingData.styleDetails ? [...existingData.styleDetails] : [];
                  
                  // 檢查樣式是否已存在
                  const styleIndex = updatedStyleBarcodes.indexOf(styleBarcode);
                  const styleDetail = {
                    image: styleImage || null,
                    livePrice: styleLivePrice || null,
                    onlinePrice: styleOnlinePrice || null,
                    costKoreanWon: styleCostKoreanWon || null,
                    costJPY: styleCostJPY || null,
                    costTWD: styleCostTWD || null,
                    costTHB: styleCostTHB || null,
                    weight: styleWeight || null
                  };
                  
                  if (styleIndex >= 0) {
                    // 更新現有樣式
                    updatedStyles[styleIndex] = style;
                    updatedRawStyleBarcodes[styleIndex] = rawStyleBarcode;
                    const existingStyleDetail = updatedStyleDetails[styleIndex] || {};
                    // 樣式圖片：如果 Excel 中有值則更新，如果為空字符串也更新為 null（清空），否則保留現有值
                    // 檢查原始值是否存在（undefined 或 null 表示 Excel 中沒有這個欄位）
                    const rawStyleImageValue = item.樣式圖片;
                    const hasStyleImageColumn = rawStyleImageValue !== undefined && rawStyleImageValue !== null;
                    // 驗證樣式圖片是否為有效的 URL，如果包含贈品格式（【】）則視為無效
                    let validStyleImage = styleImage;
                    if (hasStyleImageColumn && styleImage) {
                      const trimmedImage = String(styleImage).trim();
                      // 檢查是否包含贈品格式標記，如果包含則視為無效圖片 URL
                      if (trimmedImage.includes('【') || trimmedImage.includes('】')) {
                        validStyleImage = null; // 無效的圖片 URL，設為 null
                      } else if (!trimmedImage.startsWith('http://') && !trimmedImage.startsWith('https://')) {
                        validStyleImage = null; // 不是有效的 URL，設為 null
                      }
                    }
                    updatedStyleDetails[styleIndex] = {
                      ...existingStyleDetail,
                      // 如果Excel中有樣式圖片欄位，則更新（有值更新為值，空值更新為null）；否則保留現有值
                      image: hasStyleImageColumn ? (validStyleImage || null) : (existingStyleDetail.image || null),
                      livePrice: styleLivePrice ? styleLivePrice : (existingStyleDetail.livePrice || null),
                      onlinePrice: styleOnlinePrice ? styleOnlinePrice : (existingStyleDetail.onlinePrice || null),
                      costKoreanWon: styleCostKoreanWon ? styleCostKoreanWon : (existingStyleDetail.costKoreanWon || null),
                      costJPY: styleCostJPY ? styleCostJPY : (existingStyleDetail.costJPY || null),
                      costTWD: styleCostTWD ? styleCostTWD : (existingStyleDetail.costTWD || null),
                      costTHB: styleCostTHB ? styleCostTHB : (existingStyleDetail.costTHB || null),
                      weight: styleWeight ? styleWeight : (existingStyleDetail.weight || null)
                    };
                  } else {
                    // 新增樣式
                    updatedStyles.push(style);
                    updatedStyleBarcodes.push(styleBarcode);
                    updatedRawStyleBarcodes.push(rawStyleBarcode);
                    updatedStyleDetails.push(styleDetail);
                  }
                  
                  // 處理子商品：組合商品保留原始料號，但不修改品名（標籤在顯示時添加）
                  let finalName = item.名稱 || existingData.name;
                  let finalItemNumber = itemNumber || existingData.itemNumber || '';
                  const isComboProductMerge = subProducts.length > 0 || (existingData.subProducts && existingData.subProducts.length > 0);
                  
                  if (isComboProductMerge) {
                    // 組合商品保留自己的料號，不設為「組合商品」
                    // 品名不再添加前綴，標籤在顯示時添加
                  }
                  
                  // 處理贈品：
                  // gifts === undefined：保留現有值（Excel 中沒有贈品欄位或為空）
                  // gifts === null：刪除贈品（Excel 中贈品欄位是 '--'）
                  // gifts 有值：更新贈品（Excel 中贈品欄位有內容）
                  let finalGifts = existingData.gifts; // 預設保留現有值
                  if (gifts === null) {
                    finalGifts = null;
                  } else if (gifts !== undefined && gifts && gifts.length > 0) {
                    finalGifts = gifts;
                  }
                  // 如果 gifts === undefined，finalGifts 保持為 existingData.gifts（保留現有值）
                  
                  // 組合商品不會有樣式也不會有條碼
                  const updatedData = {
                    ...existingData,
                    image: productImage || existingData.image || '',
                  photos: buildPhotosFromImport({
                    existingPhotos: existingData.photos,
                    albumValue: item.商品相簿,
                    productImage: productImage || existingData.image,
                  }),
                    name: finalName,
                    itemNumber: finalItemNumber,
                    // 組合商品不會有樣式也不會有條碼
                    ...(isComboProductMerge ? {
                      styles: [],
                      styleBarcodes: [],
                      rawStyleBarcodes: []
                    } : {
                      styles: updatedStyles,
                      styleBarcodes: updatedStyleBarcodes,
                      rawStyleBarcodes: updatedRawStyleBarcodes,
                      // 只有在有樣式詳細信息時才設置，否則不設置（讓 cleanDataForFirestore 移除）
                      styleDetails: updatedStyleDetails
                    }),
                    vendor: vendorName || existingData.vendor || '',
                    // 子商品和贈品
                    ...(subProducts.length > 0 ? { subProducts: subProducts } : {}),
                    ...(finalGifts !== undefined ? { gifts: finalGifts } : {})
                  };
                  
                  const cleanedData = cleanDataForFirestore(updatedData);
                  await persistImportedProduct(doc(productsRef, productToMerge.id), cleanedData, existingData);
                  successCount++;
                  } else {
                    // 真正的新增商品
                    const styleDetail = {
                      image: styleImage || null,
                      livePrice: styleLivePrice || null,
                      onlinePrice: styleOnlinePrice || null,
                      costKoreanWon: styleCostKoreanWon || null,
                      costJPY: styleCostJPY || null,
                      costTWD: styleCostTWD || null,
                      costTHB: styleCostTHB || null,
                      weight: styleWeight || null
                    };
                    
                    // 處理子商品：組合商品保留原始料號，但不修改品名（標籤在顯示時添加）
                    let finalName = item.名稱 || '';
                    let finalItemNumber = itemNumber;
                    const isComboProductNew = subProducts.length > 0;
                    
                    if (isComboProductNew) {
                      // 組合商品保留自己的料號，不設為「組合商品」
                      // 品名不再添加前綴，標籤在顯示時添加
                    }
                    
                    // 處理贈品
                    let finalGifts = null;
                    if (gifts && gifts.length > 0) {
                      finalGifts = gifts;
                    }
                    
                    // 組合商品不會有樣式也不會有條碼
                    const newProduct = {
                      image: productImage || '',
                      photos: buildPhotosFromImport({
                        existingPhotos: [],
                        albumValue: item.商品相簿,
                        productImage,
                      }),
                      name: finalName,
                      itemNumber: finalItemNumber,
                      // 商品級別的價格：使用第一個樣式的價格（如果有的話）
                      livePrice: styleLivePrice || '',
                      onlinePrice: styleOnlinePrice || '',
                      remarks: item.備註 === '--' ? '' : (item.備註 || ''),
                      // 商品級別的成本和重量留空（因為Excel一行就是一個樣式，成本和重量在樣式詳細信息中）
                      costKoreanWon: '',
                      costJPY: '',
                      costTWD: '',
                      costTHB: '',
                      weight: '',
                      // 組合商品不會有樣式也不會有條碼
                      ...(isComboProductNew ? {
                        styles: [],
                        styleBarcodes: [],
                        rawStyleBarcodes: []
                      } : {
                        styles: [style],
                        styleBarcodes: [styleBarcode], // 組合條碼（條碼+料號）
                        rawStyleBarcodes: [rawStyleBarcode], // 原始條碼
                        styleDetails: [styleDetail] // 樣式詳細信息（圖片、成本、重量、價格）
                      }),
                      history: [],
                      vendor: vendorName,
                      // 子商品和贈品
                      ...(subProducts.length > 0 ? { subProducts: subProducts } : {}),
                      ...(finalGifts ? { gifts: finalGifts } : {})
                    };
                  
                  // 清理資料，移除所有 undefined 值（確保 Firestore 兼容）
                  const cleanedData = cleanDataForFirestore(newProduct);
                  
                  try {
                    const newProductRef = doc(productsRef);
                    await persistImportedProduct(newProductRef, cleanedData);
                    successCount++;
                  } catch (createError) {
                    throw new Error(`新增商品失敗: ${createError.message}`);
                  }
                }
              }

              // 更新進度
              setUploadProgress({
                current: globalIndex + 1,
                total,
                status: 'uploading',
                successCount,
                errorCount
              });
            } catch (itemError) {
              errorCount++;
              const errorMsg = `第 ${legacyImportRowNumber(item, originalIndex)} 列 (${item.名稱 || item.料號 || '未知'}): ${itemError.message}`;
              errors.push(errorMsg);
              console.error(errorMsg, itemError);
              
              setUploadProgress({
                current: globalIndex + 1,
                total,
                status: 'uploading',
                successCount,
                errorCount
              });
              setUploadErrors([...errors]);
              }
            }
          } else {
            // 不同料號仍可能由相同條碼指向同一商品；必須逐列完成再繼續。
            for (const { item, originalIndex } of batch) {
              const globalIndex = originalIndex;
              try {
                const { itemNumber } = readLegacyImportIdentity(item);
                const style = item.樣式 ? item.樣式 : '無樣式';
                const { rawStyleBarcode } = readLegacyImportIdentity(item); // 原始條碼
                let styleBarcode = rawStyleBarcode ? combineBarcode(rawStyleBarcode, itemNumber) : ''; // 組合條碼（條碼+料號），如果沒有條碼則為空
                // 樣式圖片（可選）- 確保正確讀取和處理
                // 驗證樣式圖片是否為有效的 URL，如果包含贈品格式（【】）則視為無效
                let styleImage = '';
                if (item.樣式圖片 !== undefined && item.樣式圖片 !== null) {
                  const rawStyleImage = String(item.樣式圖片).trim();
                  // 檢查是否包含贈品格式標記，如果包含則視為無效圖片 URL
                  if (rawStyleImage && !rawStyleImage.includes('【') && !rawStyleImage.includes('】')) {
                    // 簡單驗證是否為 URL 格式（以 http:// 或 https:// 開頭）
                    if (rawStyleImage.startsWith('http://') || rawStyleImage.startsWith('https://')) {
                      styleImage = rawStyleImage;
                    }
                  }
                }
                // 樣式級別的價格：直接使用直播價、商成價作為樣式價格（Excel一行就是一個樣式）
                const styleLivePrice = String(item.直播價 || '').trim();
                const styleOnlinePrice = String(item.商成價 || '').trim();
                // 使用原本的成本和重量欄位作為樣式成本和重量（Excel一行就是一個樣式）
                const styleCostKoreanWon = String(item.成本韓幣 || '').trim(); // 樣式成本韓幣
                const styleCostJPY = String(item.成本日幣 || '').trim(); // 樣式成本日幣
                const styleCostTWD = String(item.成本台幣 || '').trim(); // 樣式成本台幣
                const styleCostTHB = String(item.成本泰銖 || '').trim(); // 樣式成本泰銖
                const styleWeight = String(item.重量 || '').trim(); // 樣式重量
                const vendorName = String(item.廠商 || '').trim();
                
                // 處理主要商品圖片（單一圖片）
                const productImage = String(item.圖片 || '').trim();
                
                // 處理子商品欄位
                const subProductsStr = String(item.子商品 || '').trim();
                const subProducts = parseSubProductsOrGifts(subProductsStr);
                
                // 處理贈品欄位
                // 如果 Excel 中沒有「贈品」欄位（undefined），則保留現有值（設為 undefined）
                // 如果 Excel 中有「贈品」欄位但為空字符串，則保留現有值（設為 undefined）
                // 如果 Excel 中「贈品」欄位是 '--' 或 '—'，則刪除贈品（設為 null）
                // 如果 Excel 中「贈品」欄位有值，則更新贈品
                let gifts = undefined; // undefined 表示保留現有值
                if (item.贈品 !== undefined && item.贈品 !== null) {
                  const giftsStr = String(item.贈品).trim();
                  if (giftsStr === '--' || giftsStr === '—') {
                    gifts = null; // null 表示刪除贈品
                  } else if (giftsStr) {
                    gifts = parseSubProductsOrGifts(giftsStr); // 有值則更新
                  }
                  // 如果 giftsStr 為空字符串，gifts 保持為 undefined（保留現有值）
                }

                // 處理廠商（如果不存在則新增）
                const matchedImportProduct = await findCurrentImportProduct(item, originalIndex);
                if (matchedImportProduct && rawStyleBarcode && !itemNumber) {
                  styleBarcode = combineBarcode(rawStyleBarcode, matchedImportProduct.data().itemNumber || '');
                }

                if (vendorName !== '' && !existingVendors[vendorName]) {
                  try {
                    await addDoc(vendorsRef, { name: vendorName, country });
                    existingVendors[vendorName] = true;
                  } catch (vendorError) {
                    console.warn(`新增廠商失敗: ${vendorName}`, vendorError);
                    // 不中斷流程，繼續處理商品
                  }
                }

                // 查詢現有商品（與上面相同的邏輯）
                let existingProduct = matchedImportProduct;
                try {
                  // 先查組合條碼（條碼+料號）- 最精確的匹配
                  if (!existingProduct && styleBarcode) {
                    const existingProductByBarcode = await getDocs(
                      query(productsRef, where('styleBarcodes', 'array-contains', styleBarcode))
                    );
                    if (!existingProductByBarcode.empty) {
                      const candidate = existingProductByBarcode.docs[0];
                      const candidateData = candidate.data();
                      if (candidateData && (candidateData.name || candidateData.itemNumber)) {
                        existingProduct = candidate;
                      }
                    }
                  }
                  
                  // 如果沒找到，再查原始條碼（但必須料號也匹配，避免錯誤合併）
                  if (!existingProduct && rawStyleBarcode) {
                    const existingProductsByRawBarcode = await getDocs(
                      query(productsRef, where('styleBarcodes', 'array-contains', rawStyleBarcode))
                    );
                    
                    if (!existingProductsByRawBarcode.empty) {
                      let matchedProduct = existingProductsByRawBarcode.docs.find(doc => {
                        const data = doc.data();
                        if (!data || (!data.name && !data.itemNumber)) {
                          return false;
                        }
                        if (data.rawStyleBarcodes && data.rawStyleBarcodes.length > 0) {
                          return data.rawStyleBarcodes.includes(rawStyleBarcode) && 
                                 data.itemNumber === itemNumber;
                        }
                        return data.itemNumber === itemNumber;
                      });
                      
                      if (matchedProduct) {
                        existingProduct = matchedProduct;
                      } else if (!itemNumber) {
                        const firstCandidate = existingProductsByRawBarcode.docs[0];
                        const firstData = firstCandidate.data();
                        if (firstData && (firstData.name || firstData.itemNumber)) {
                          existingProduct = firstCandidate;
                        }
                      }
                    }
                  }
                  
                  // 如果沒找到，再查料號（只根據料號匹配）
                  if (!existingProduct && itemNumber) {
                    const existingProductByItemNumber = await getDocs(
                      query(productsRef, where('itemNumber', '==', itemNumber))
                    );
                    if (!existingProductByItemNumber.empty) {
                      const candidate = existingProductByItemNumber.docs[0];
                      const candidateData = candidate.data();
                      if (candidateData && (candidateData.name || candidateData.itemNumber)) {
                        existingProduct = candidate;
                      }
                    }
                  }
                } catch (queryError) {
                  throw new Error(`查詢商品失敗: ${queryError.message}`);
                }

                // 處理商品更新或新增（與上面相同的邏輯）
                // 這裡需要包含完整的處理邏輯，與順序處理的部分相同
                // 為了避免代碼重複，我們將使用相同的邏輯
                // 注意：由於已經按料號分組，這裡不會有相同料號的商品並行處理
                
                // 這裡的處理邏輯與順序處理部分完全相同（第259-539行）
                // 為了簡化，我們直接複製相同的邏輯
                if (existingProduct) {
                  // 更新現有商品（與順序處理部分相同的邏輯）
                  const existingData = existingProduct.data();
                  const updatedStyles = existingData.styles ? [...existingData.styles] : [];
                  const updatedStyleBarcodes = existingData.styleBarcodes ? [...existingData.styleBarcodes] : [];
                  const updatedRawStyleBarcodes = existingData.rawStyleBarcodes ? [...existingData.rawStyleBarcodes] : [];
                  const updatedStyleDetails = existingData.styleDetails ? [...existingData.styleDetails] : [];
                  
                  // 如果沒有條碼，嘗試根據樣式名稱匹配，或使用第一個樣式
                  let styleIndex = -1;
                  
                  if (styleBarcode) {
                    // 有條碼，使用條碼匹配
                    styleIndex = updatedStyleBarcodes.indexOf(styleBarcode);
                  } else if (style && style !== '無樣式') {
                    // 沒有條碼，但有樣式名稱，根據樣式名稱匹配
                    styleIndex = updatedStyles.findIndex(s => s === style);
                  } else if (updatedStyles.length === 1) {
                    // 沒有條碼和樣式名稱，但商品只有一個樣式，使用第一個樣式
                    styleIndex = 0;
                  } else if (updatedStyles.length > 1) {
                    // 沒有條碼和樣式名稱，但商品有多個樣式，使用第一個樣式（更新商品級別資訊）
                    styleIndex = 0;
                  }
                  
                  let styleHistoryEntry = null;
                  
                  if (styleIndex >= 0) {
                    const existingStyleDetail = updatedStyleDetails[styleIndex] || {};
                    const beforeState = {
                      image: existingStyleDetail.image || null,
                      livePrice: existingStyleDetail.livePrice || null,
                      onlinePrice: existingStyleDetail.onlinePrice || null,
                      costKoreanWon: existingStyleDetail.costKoreanWon || null,
                      costJPY: existingStyleDetail.costJPY || null,
                      costTWD: existingStyleDetail.costTWD || null,
                      costTHB: existingStyleDetail.costTHB || null,
                      weight: existingStyleDetail.weight || null
                    };
                    
                    const rawStyleImageValue = item.樣式圖片;
                    const hasStyleImageColumn = rawStyleImageValue !== undefined && rawStyleImageValue !== null;
                    const afterImage = hasStyleImageColumn ? (styleImage || null) : (existingStyleDetail.image || null);
                    
                    const afterState = {
                      image: afterImage,
                      livePrice: styleLivePrice ? styleLivePrice : (existingStyleDetail.livePrice || null),
                      onlinePrice: styleOnlinePrice ? styleOnlinePrice : (existingStyleDetail.onlinePrice || null),
                      costKoreanWon: styleCostKoreanWon ? styleCostKoreanWon : (existingStyleDetail.costKoreanWon || null),
                      costJPY: styleCostJPY ? styleCostJPY : (existingStyleDetail.costJPY || null),
                      costTWD: styleCostTWD ? styleCostTWD : (existingStyleDetail.costTWD || null),
                      costTHB: styleCostTHB ? styleCostTHB : (existingStyleDetail.costTHB || null),
                      weight: styleWeight ? styleWeight : (existingStyleDetail.weight || null)
                    };
                    
                    const hasChanges = JSON.stringify(beforeState) !== JSON.stringify(afterState);
                    if (hasChanges) {
                      styleHistoryEntry = {
                        date: new Date().toLocaleDateString(),
                        style: style,
                        styleIndex: styleIndex,
                        before: beforeState,
                        after: afterState
                      };
                    }
                    
                    updatedStyles[styleIndex] = style;
                    updatedRawStyleBarcodes[styleIndex] = rawStyleBarcode;
                    updatedStyleDetails[styleIndex] = afterState;
                  } else {
                    const styleDetail = {
                      image: styleImage || null,
                      livePrice: styleLivePrice || null,
                      onlinePrice: styleOnlinePrice || null,
                      costKoreanWon: styleCostKoreanWon || null,
                      costJPY: styleCostJPY || null,
                      costTWD: styleCostTWD || null,
                      costTHB: styleCostTHB || null,
                      weight: styleWeight || null
                    };
                    
                      // 歷史記錄不包含圖片
                      const styleDetailForHistory = {
                        livePrice: styleLivePrice || null,
                        onlinePrice: styleOnlinePrice || null,
                        costKoreanWon: styleCostKoreanWon || null,
                        costJPY: styleCostJPY || null,
                        costTWD: styleCostTWD || null,
                        costTHB: styleCostTHB || null,
                        weight: styleWeight || null
                      };
                      
                      styleHistoryEntry = {
                        date: new Date().toLocaleDateString(),
                        style: style,
                        styleIndex: updatedStyles.length,
                        before: null,
                        after: styleDetailForHistory
                      };
                    
            updatedStyles.push(style);
            updatedStyleBarcodes.push(styleBarcode);
                    updatedRawStyleBarcodes.push(rawStyleBarcode);
                    updatedStyleDetails.push(styleDetail);
                  }
                  
                  const hasStyleImages = updatedStyleDetails.some(detail => detail && detail.image);
                  const hasStylePrices = updatedStyleDetails.some(detail => detail && (detail.livePrice || detail.onlinePrice));
                  const hasStyleCosts = updatedStyleDetails.some(detail => detail && (detail.costKoreanWon || detail.costJPY || detail.costTWD || detail.costTHB || detail.weight));
                  
                  const historyEntry = {
                    date: new Date().toLocaleDateString(),
                    costKoreanWon: existingData.costKoreanWon,
                    costJPY: existingData.costJPY,
                    costTWD: existingData.costTWD,
                    costTHB: existingData.costTHB,
                    livePrice: existingData.livePrice,
                    onlinePrice: existingData.onlinePrice,
                    remarks: existingData.remarks
                  };
                  
                  const newHistory = [...(existingData.history || []), historyEntry];
                  if (styleHistoryEntry) {
                    newHistory.push(styleHistoryEntry);
                  }
                  
                  // 處理子商品：組合商品保留原始料號，但不修改品名（標籤在顯示時添加）
                  let finalName = item.名稱 || existingData.name;
                  let finalItemNumber = itemNumber || existingData.itemNumber || '';
                  const isComboProductMerge = subProducts.length > 0 || (existingData.subProducts && existingData.subProducts.length > 0);
                  
                  if (isComboProductMerge) {
                    // 組合商品保留自己的料號，不設為「組合商品」
                    // 品名不再添加前綴，標籤在顯示時添加
                  }
                  
                  // 處理贈品：
                  // gifts === undefined：保留現有值（Excel 中沒有贈品欄位或為空）
                  // gifts === null：刪除贈品（Excel 中贈品欄位是 '--' 或 '—'）
                  // gifts 有值：更新贈品（Excel 中贈品欄位有內容）
                  let finalGifts = existingData.gifts; // 預設保留現有值
                  if (gifts === null) {
                    finalGifts = null;
                  } else if (gifts !== undefined && gifts && gifts.length > 0) {
                    finalGifts = gifts;
                  }
                  // 如果 gifts === undefined，finalGifts 保持為 existingData.gifts（保留現有值）
                  
                  // 組合商品不會有樣式也不會有條碼
          const updatedData = {
            ...existingData,
                    history: newHistory,
                    image: productImage || existingData.image || '',
                  photos: buildPhotosFromImport({
                    existingPhotos: existingData.photos,
                    albumValue: item.商品相簿,
                    productImage: productImage || existingData.image,
                  }),
                    name: finalName,
                    itemNumber: finalItemNumber,
                    livePrice: existingData.livePrice || '',
                    onlinePrice: existingData.onlinePrice || '',
            remarks: item.備註 === '--' ? '' : (item.備註 || existingData.remarks),
                    // 組合商品不會有樣式也不會有條碼
                    ...(isComboProductMerge ? {
                      styles: [],
                      styleBarcodes: [],
                      rawStyleBarcodes: []
                    } : {
            styles: updatedStyles,
            styleBarcodes: updatedStyleBarcodes,
                      rawStyleBarcodes: updatedRawStyleBarcodes,
                      styleDetails: updatedStyleDetails
                    }),
                    vendor: vendorName || existingData.vendor || '',
                    enableMultiImage: hasStyleImages || existingData.enableMultiImage || false,
                    enableMultiPrice: hasStylePrices || existingData.enableMultiPrice || false,
                    enableMultiCostWeight: hasStyleCosts || existingData.enableMultiCostWeight || false,
                    // 子商品和贈品
                    ...(subProducts.length > 0 ? { subProducts: subProducts } : {}),
                    ...(finalGifts !== undefined ? { gifts: finalGifts } : {})
          };
          if (!existingData.itemNumber && itemNumber) {
            updatedData.itemNumber = itemNumber;
          }
                  
                  const cleanedData = cleanDataForFirestore(updatedData);
                  await persistImportedProduct(doc(productsRef, existingProduct.id), cleanedData, existingData);
                  successCount++;
        } else {
                  // 新增商品前，先檢查是否有相同料號的商品（自動合併）
                  // 注意：組合商品（有子商品）不可被合併
                  let productToMerge = null;
                  const isComboProductNew = subProducts.length > 0;
                  if (itemNumber && !isComboProductNew) {
                    try {
                      const existingProductByItemNumber = await getDocs(
                        query(productsRef, where('itemNumber', '==', itemNumber))
                      );
                      if (!existingProductByItemNumber.empty) {
                        const candidate = existingProductByItemNumber.docs[0];
                        const candidateData = candidate.data();
                        // 組合商品不可被合併，也不可合併其他商品（根據是否有子商品判斷）
                        if (candidateData && (candidateData.name || candidateData.itemNumber) && 
                            !candidateData.subProducts) {
                          productToMerge = candidate;
                        }
                      }
                    } catch (mergeQueryError) {
                      console.warn(`查詢相同料號商品失敗: ${mergeQueryError.message}`);
                    }
                  }
                  
                  if (productToMerge) {
                    const existingData = productToMerge.data();
                    const updatedStyles = existingData.styles ? [...existingData.styles] : [];
                    const updatedStyleBarcodes = existingData.styleBarcodes ? [...existingData.styleBarcodes] : [];
                    const updatedRawStyleBarcodes = existingData.rawStyleBarcodes ? [...existingData.rawStyleBarcodes] : [];
                    const updatedStyleDetails = existingData.styleDetails ? [...existingData.styleDetails] : [];
                    
                    const styleIndex = updatedStyleBarcodes.indexOf(styleBarcode);
                    const styleDetail = {
                      image: styleImage || null,
                      livePrice: styleLivePrice || null,
                      onlinePrice: styleOnlinePrice || null,
                      costKoreanWon: styleCostKoreanWon || null,
                      costJPY: styleCostJPY || null,
                      costTWD: styleCostTWD || null,
                      costTHB: styleCostTHB || null,
                      weight: styleWeight || null
                    };
                    
                    if (styleIndex >= 0) {
                      // 更新現有樣式
                      updatedStyles[styleIndex] = style;
                      // 如果有條碼，更新條碼；如果沒有條碼，保留現有條碼
                      if (rawStyleBarcode) {
                        updatedRawStyleBarcodes[styleIndex] = rawStyleBarcode;
                        updatedStyleBarcodes[styleIndex] = styleBarcode;
                      }
                      const existingStyleDetail = updatedStyleDetails[styleIndex] || {};
                      const rawStyleImageValue = item.樣式圖片;
                      const hasStyleImageColumn = rawStyleImageValue !== undefined && rawStyleImageValue !== null;
                      updatedStyleDetails[styleIndex] = {
                        ...existingStyleDetail,
                        image: hasStyleImageColumn ? (styleImage || null) : (existingStyleDetail.image || null),
                        livePrice: styleLivePrice ? styleLivePrice : (existingStyleDetail.livePrice || null),
                        onlinePrice: styleOnlinePrice ? styleOnlinePrice : (existingStyleDetail.onlinePrice || null),
                        costKoreanWon: styleCostKoreanWon ? styleCostKoreanWon : (existingStyleDetail.costKoreanWon || null),
                        costJPY: styleCostJPY ? styleCostJPY : (existingStyleDetail.costJPY || null),
                        costTWD: styleCostTWD ? styleCostTWD : (existingStyleDetail.costTWD || null),
                        costTHB: styleCostTHB ? styleCostTHB : (existingStyleDetail.costTHB || null),
                        weight: styleWeight ? styleWeight : (existingStyleDetail.weight || null)
                      };
                    } else {
                      // 新增樣式（只有在有條碼或樣式名稱時才新增）
                      if (styleBarcode || (style && style !== '無樣式')) {
                        updatedStyles.push(style);
                        updatedStyleBarcodes.push(styleBarcode || '');
                        updatedRawStyleBarcodes.push(rawStyleBarcode || '');
                        updatedStyleDetails.push(styleDetail);
                      }
                      // 如果沒有條碼也沒有樣式名稱，就不新增樣式，只更新商品級別資訊
                    }
                    
                    // 處理子商品和贈品
                    let finalName = item.名稱 || existingData.name;
                    let finalItemNumber = itemNumber || existingData.itemNumber || '';
                    const isComboProductParallel = subProducts.length > 0 || (existingData.subProducts && existingData.subProducts.length > 0);
                    
                    if (isComboProductParallel) {
                      // 組合商品保留自己的料號，不設為「組合商品」
                      // 品名不再添加前綴，標籤在顯示時添加
                    }
                    
                    let finalGifts = existingData.gifts;
                    if (gifts === null) {
                      finalGifts = null;
                    } else if (gifts && gifts.length > 0) {
                      finalGifts = gifts;
                    } else if (gifts !== undefined) {
                      finalGifts = gifts;
                    }
                    
                    // 組合商品不會有樣式也不會有條碼
                    const updatedData = {
                      ...existingData,
                      image: productImage || existingData.image || '',
                  photos: buildPhotosFromImport({
                    existingPhotos: existingData.photos,
                    albumValue: item.商品相簿,
                    productImage: productImage || existingData.image,
                  }),
                      name: finalName,
                      itemNumber: finalItemNumber,
                      remarks: item.備註 === '--' ? '' : (item.備註 || existingData.remarks),
                      // 組合商品不會有樣式也不會有條碼
                      ...(isComboProductParallel ? {
                        styles: [],
                        styleBarcodes: [],
                        rawStyleBarcodes: []
                      } : {
                        styles: updatedStyles,
                        styleBarcodes: updatedStyleBarcodes,
                        rawStyleBarcodes: updatedRawStyleBarcodes,
                        styleDetails: updatedStyleDetails
                      }),
                      vendor: vendorName || existingData.vendor || '',
                      // 子商品和贈品
                      ...(subProducts.length > 0 ? { subProducts: subProducts } : {}),
                      ...(finalGifts !== undefined ? { gifts: finalGifts } : {})
                    };
                    
                    const cleanedData = cleanDataForFirestore(updatedData);
                    await persistImportedProduct(doc(productsRef, productToMerge.id), cleanedData, existingData);
                    successCount++;
                  } else {
                    const styleDetail = {
                      image: styleImage || null,
                      livePrice: styleLivePrice || null,
                      onlinePrice: styleOnlinePrice || null,
                      costKoreanWon: styleCostKoreanWon || null,
                      costJPY: styleCostJPY || null,
                      costTWD: styleCostTWD || null,
                      costTHB: styleCostTHB || null,
                      weight: styleWeight || null
                    };
                    
                    // 處理子商品：組合商品保留原始料號，但不修改品名（標籤在顯示時添加）
                    let finalName = item.名稱 || '';
                    let finalItemNumber = itemNumber;
                    const isComboProductNewParallel = subProducts.length > 0;
                    
                    if (isComboProductNewParallel) {
                      // 組合商品保留自己的料號，不設為「組合商品」
                      // 品名不再添加前綴，標籤在顯示時添加
                    }
                    
                    // 處理贈品
                    let finalGifts = null;
                    if (gifts && gifts.length > 0) {
                      finalGifts = gifts;
                    }
                    
                    // 組合商品不會有樣式也不會有條碼
          const newProduct = {
                      image: productImage || '',
                      photos: buildPhotosFromImport({
                        existingPhotos: [],
                        albumValue: item.商品相簿,
                        productImage,
                      }),
                      name: finalName,
                      itemNumber: finalItemNumber,
                      livePrice: styleLivePrice || '',
                      onlinePrice: styleOnlinePrice || '',
                      remarks: item.備註 === '--' ? '' : (item.備註 || ''),
                      costKoreanWon: '',
                      costJPY: '',
                      costTWD: '',
                      costTHB: '',
                      weight: '',
                      // 組合商品不會有樣式也不會有條碼
                      ...(isComboProductNewParallel ? {
                        styles: [],
                        styleBarcodes: [],
                        rawStyleBarcodes: []
                      } : {
            styles: [style],
            styleBarcodes: [styleBarcode],
                        rawStyleBarcodes: [rawStyleBarcode],
                        styleDetails: [styleDetail]
                      }),
            history: [],
                      vendor: vendorName,
                      // 子商品和贈品
                      ...(subProducts.length > 0 ? { subProducts: subProducts } : {}),
                      ...(finalGifts ? { gifts: finalGifts } : {})
          };
                    
                    const cleanedData = cleanDataForFirestore(newProduct);
          const newProductRef = doc(productsRef);
                    await persistImportedProduct(newProductRef, cleanedData);
                    successCount++;
                  }
                }
                
                // 更新進度
                setUploadProgress({
                  current: globalIndex + 1,
                  total,
                  status: 'uploading',
                  successCount,
                  errorCount
                });

              } catch (itemError) {
                errorCount++;
                const errorMsg = `第 ${legacyImportRowNumber(item, originalIndex)} 列 (${item.名稱 || item.料號 || '未知'}): ${itemError.message}`;
                errors.push(errorMsg);
                console.error(errorMsg, itemError);
                
                setUploadProgress({
                  current: globalIndex + 1,
                  total,
                  status: 'uploading',
                  successCount,
                  errorCount
                });
                setUploadErrors([...errors]);
              }
            }
          }

          // 批次之間稍作延遲，避免過度負載
          if (i + batchSize < sortedItems.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        setUploadProgress({ current: total, total, status: 'completed', successCount, errorCount });
        
        // 顯示結果
        if (errorCount > 0) {
          const errorSummary = `上傳完成！成功: ${successCount} 筆，失敗: ${errorCount} 筆。請查看下方錯誤詳情`;
          handleWarning(errorSummary, 5000);
        } else {
          handleSuccess(`上傳成功！共處理 ${successCount} 筆資料`, 3000);
          // 觸發重新載入數據，而不是刷新整個頁面
          setRefreshKey(prev => prev + 1);
          setFile(null);
        }
      } catch (error) {
        if (error.code === 'legacy-import-preflight') setUploadErrors(error.issues);
        handleError(error, '批次上傳');
        setUploadProgress({ current: 0, total: error.totalRows || 0, status: 'error', error: error.message });
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = () => {
      handleError(new Error('讀取檔案失敗'), '檔案讀取');
      setIsUploading(false);
      setUploadProgress(null);
    };

    reader.readAsArrayBuffer(file);
  };

  // 下載文本文件
  const downloadTextFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const readExportRates = async () => {
    if (country !== 'korea') return null;
    const rateDoc = await getDoc(doc(db, 'rates', 'korea'));
    return rateDoc.exists() ? rateDoc.data() : null;
  };
  const koreanExportCosts = (product, variant, rates) => {
    if (country !== 'korea') return {};
    const costs = koreaNumericValues({product,detailContext:{rates:{data:rates}}}, variant);
    return {成本韓幣:costs.costKRW,成本台幣:costs.costTWD};
  };
  const handleExport = async () => {
    if (exportStatus) return;
    setExportStatus('正在準備匯出…');
    try {
    const { buildProductWorkbook, downloadWorkbookBuffer } = await import('../utils/productWorkbook');
    let imageCount = 0;
    let warningCount = 0;
    const productsRef = collection(db, collectionName);
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    if (!products.length) { handleInfo('沒有商品可匯出'); return; }
    const exportRates = await readExportRates();
    const maxLength = 32767;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const maxRowsPerFile = 500; // 含圖匯出分檔，限制瀏覽器記憶體使用
    const allLongTextData = []; // 存儲所有超長文本數據
    
    // 收集所有格式化數據
    const allFormattedProducts = [];
    let globalRowIndex = 0; // 全局行號計數器
    
    products.forEach(product => {
      const styles = Array.isArray(product.styles) ? product.styles : [];
      const styleBarcodes = Array.isArray(product.styleBarcodes) ? product.styleBarcodes : [];
      const rawStyleBarcodes = Array.isArray(product.rawStyleBarcodes) ? product.rawStyleBarcodes : [];
      const styleDetails = Array.isArray(product.styleDetails) ? product.styleDetails : [];
      
      // 格式化子商品和贈品為字串
      const formatSubProducts = (subProducts) => {
        if (!subProducts || subProducts.length === 0) return '';
        return subProducts.map(sp => {
          const style = sp.style && sp.style !== '--' ? `(${sp.style})` : '(--)';
          return `${sp.itemNumber}${style}*${sp.quantity || 1}`;
        }).join(',');
      };
      
      const formatGifts = (gifts) => {
        if (!gifts || gifts.length === 0) return '';
        // 按標題分組贈品
        const groupedByTitle = {};
        gifts.forEach(gift => {
          const title = gift.title || '';
          if (!groupedByTitle[title]) {
            groupedByTitle[title] = [];
          }
          groupedByTitle[title].push(gift);
        });
        
        // 格式化每個分組
        return Object.values(groupedByTitle).map(group => {
          return group.map(gift => {
            const title = gift.title ? `【${gift.title}】` : '';
            const style = gift.style && gift.style !== '--' ? `(${gift.style})` : '(--)';
            return `${title}${gift.itemNumber}${style}*${gift.quantity || 1}`;
          }).join('\n');
        }).join('\n');
      };
      
      // 獲取贈品模式（如果所有贈品都有相同的模式）
      const getGiftMode = (gifts) => {
        if (!gifts || gifts.length === 0) return '';
        const modes = gifts.map(g => g.mode).filter(m => m);
        if (modes.length > 0 && modes.every(m => m === modes[0])) {
          return modes[0];
        }
        return '';
      };
      
      const subProductsStr = formatSubProducts(product.subProducts);
      const giftsStr = formatGifts(product.gifts);
      
      // 如果商品沒有樣式，至少匯出一行（用於組合商品）
      if (styles.length === 0) {
        globalRowIndex++;
        const giftModeStr = getGiftMode(product.gifts);
        const rowData = {
          圖片: product.image ?? '',
          名稱: product.name ?? '',
          料號: product.itemNumber ?? '',
          直播價: product.livePrice ?? '',
          商成價: product.onlinePrice ?? '',
          贈品模式: giftModeStr,
          備註: product.remarks ?? '',
          成本韓幣: '',
          成本日幣: '',
          成本台幣: '',
          成本泰銖: '',
          ...koreanExportCosts(product, {}, exportRates),
          重量: productExportWeight(product).value,
          樣式: '',
          樣式條碼: '',
          樣式圖片: '',
          子商品: subProductsStr,
          贈品: giftsStr,
          廠商: product.vendor ?? ''
        };
        
        // 檢查每個字段是否超過限制
        const rowIndex = globalRowIndex + 1;
        const fileIndexValue = (allFormattedProducts.length) / maxRowsPerFile;
        const currentFileIndex = fileIndexValue >= 0 ? (fileIndexValue | 0) : 0;
        
        Object.keys(rowData).forEach(key => {
          const value = String(rowData[key]);
          if (value.length > maxLength) {
            allLongTextData.push({
              row: rowIndex,
              field: key,
              value: value,
              productId: product.id,
              itemNumber: product.itemNumber,
              name: product.name,
              fileIndex: currentFileIndex
            });
            if (!['圖片', '樣式圖片'].includes(key)) rowData[key] = value.slice(0, /[\uD800-\uDBFF]/.test(value[32766]) ? 32766 : 32767);
          }
        });
        
        allFormattedProducts.push(rowData);
      } else {
      styles.forEach((style, index) => {
        globalRowIndex++;
          // 匯出時使用原始條碼（不顯示料號）
          const displayBarcode = rawStyleBarcodes[index] || extractRawBarcode(styleBarcodes[index], product.itemNumber ?? '');
          const styleDetail = styleDetails[index];
          // Excel一行就是一個樣式，成本和重量欄位代表該樣式的成本和重量
        const giftModeStr = getGiftMode(product.gifts);
        const rowData = {
          圖片: product.image ?? '',
          名稱: product.name ?? '',
          料號: product.itemNumber ?? '',
          直播價: product.livePrice ?? '',
          商成價: product.onlinePrice ?? '',
          贈品模式: giftModeStr,
          備註: product.remarks ?? '',
            成本韓幣: styleDetail?.costKoreanWon ?? '', // 樣式成本（Excel一行就是一個樣式）
            成本日幣: styleDetail?.costJPY ?? '', // 樣式成本（Excel一行就是一個樣式）
            成本台幣: styleDetail?.costTWD ?? '', // 樣式成本（Excel一行就是一個樣式）
            成本泰銖: styleDetail?.costTHB ?? '', // 樣式成本（Excel一行就是一個樣式）
            ...koreanExportCosts(product, styleDetail || {}, exportRates),
            重量: index === 0 ? productExportWeight(product).value : null,
          樣式: style || '',
            樣式條碼: displayBarcode || '', // 使用原始條碼
            樣式圖片: styleDetail?.image ?? '', // 樣式圖片
            子商品: subProductsStr, // 子商品（每個樣式行都顯示）
            贈品: giftsStr, // 贈品（每個樣式行都顯示）
          廠商: product.vendor ?? ''
        };

        // 檢查每個字段是否超過限制
        const rowIndex = globalRowIndex + 1; // Excel 行號（+1 因為表頭是第1行）
        const fileIndexValue = (allFormattedProducts.length) / maxRowsPerFile;
        const currentFileIndex = fileIndexValue >= 0 ? (fileIndexValue | 0) : 0; // 當前行屬於哪個檔案
        
        Object.keys(rowData).forEach(key => {
          const value = String(rowData[key]);
          if (value.length > maxLength) {
            // 記錄超長數據
            allLongTextData.push({
              row: rowIndex,
              field: key,
              value: value,
              productId: product.id,
              itemNumber: product.itemNumber,
              name: product.name,
              fileIndex: currentFileIndex // 記錄屬於哪個檔案
            });
            // 在 Excel 中用引用替換
            if (!['圖片', '樣式圖片'].includes(key)) rowData[key] = value.slice(0, /[\uD800-\uDBFF]/.test(value[32766]) ? 32766 : 32767);
          }
        });

        allFormattedProducts.push(rowData);
      });
      }
    });

    // 將數據分成多個檔案（每個最多5000筆）
    const totalFilesCalc = allFormattedProducts.length / maxRowsPerFile;
    const totalFiles = totalFilesCalc > 0 ? (totalFilesCalc % 1 === 0 ? totalFilesCalc : (totalFilesCalc | 0) + 1) : 1;
    
    for (let fileIndex = 0; fileIndex < totalFiles; fileIndex++) {
      const startIndex = fileIndex * maxRowsPerFile;
      const calculatedEndIndex = startIndex + maxRowsPerFile;
      const endIndex = calculatedEndIndex < allFormattedProducts.length ? calculatedEndIndex : allFormattedProducts.length;
      const formattedProducts = allFormattedProducts.slice(startIndex, endIndex);
      
      const { workbook, images, warnings } = await buildProductWorkbook(formattedProducts, {
        onProgress: (done, total, phase) => setExportStatus(`正在${phase === 'images' ? '讀取圖片' : '寫入資料'}：第 ${fileIndex + 1}/${totalFiles} 份，${done}/${total}`),
      });
      imageCount += images;
      warningCount += warnings.length;
      if (warnings.length) handleInfo(`${warnings.length} 個圖片欄位未能嵌入：\n${warnings.map(w=>`第 ${w.row} 列 ${w.field}：${w.reason}`).join('\n')}`);
      const fileName = totalFiles > 1
        ? `商品匯出_${timestamp}_第${fileIndex + 1}部分_共${totalFiles}部分_${formattedProducts.length}筆.xlsx`
        : `商品匯出_${timestamp}_${formattedProducts.length}筆.xlsx`;
      downloadWorkbookBuffer(await workbook.xlsx.writeBuffer(), fileName);

    }

    // 如果有超長文本，延遲後生成單獨的文本文件（確保 Excel 文件先下載）
    if (allLongTextData.length > 0) {
      setTimeout(() => {
        // 按字段分組生成文件
        const filesByField = {};
        allLongTextData.forEach(item => {
          if (!filesByField[item.field]) {
            filesByField[item.field] = [];
          }
          filesByField[item.field].push(item);
        });

        // 為每個超長字段生成一個文件
        const fieldKeys = Object.keys(filesByField);
        fieldKeys.forEach((field, fieldIndex) => {
          setTimeout(() => {
            const items = filesByField[field];
            let content = `商品匯出 - ${field} 超長數據\n`;
            content += `生成時間: ${new Date().toLocaleString()}\n`;
            content += `共 ${items.length} 筆超長數據\n`;
            content += '='.repeat(80) + '\n\n';

            items.forEach((item, idx) => {
              content += `[${idx + 1}] 第 ${item.row} 行 (檔案${item.fileIndex + 1})\n`;
              content += `商品ID: ${item.productId}\n`;
              content += `料號: ${item.itemNumber}\n`;
              content += `名稱: ${item.name}\n`;
              content += `字段: ${item.field}\n`;
              content += `內容長度: ${item.value.length} 字符\n`;
              content += '-'.repeat(80) + '\n';
              content += item.value + '\n';
              content += '='.repeat(80) + '\n\n';
            });

            downloadTextFile(content, `商品匯出_${field}_超長數據_${timestamp}.txt`);
          }, (totalFiles * 300) + (fieldIndex * 300)); // 在所有Excel文件之後下載
        });

        // 生成索引文件（最後下載）
        setTimeout(() => {
          let indexContent = `商品匯出 - 超長數據索引\n`;
          indexContent += `生成時間: ${new Date().toLocaleString()}\n`;
          indexContent += `共 ${allLongTextData.length} 筆超長數據\n`;
          indexContent += `共 ${totalFiles} 個Excel檔案\n`;
          indexContent += '='.repeat(80) + '\n\n';
          allLongTextData.forEach((item, idx) => {
            indexContent += `[${idx + 1}] 第 ${item.row} 行 (檔案${item.fileIndex + 1}) - ${item.field}\n`;
            indexContent += `  商品ID: ${item.productId}\n`;
            indexContent += `  料號: ${item.itemNumber}\n`;
            indexContent += `  名稱: ${item.name}\n`;
            indexContent += `  內容長度: ${item.value.length} 字符\n`;
            indexContent += `  見文件: 商品匯出_${item.field}_超長數據_${timestamp}.txt\n`;
            indexContent += '\n';
          });
          downloadTextFile(indexContent, `商品匯出_超長數據索引_${timestamp}.txt`);
        }, (totalFiles * 300) + (fieldKeys.length * 300) + 100); // 在所有字段文件之後下載
      }, (totalFiles * 300) + 200); // 延遲確保所有 Excel 文件先下載
    }
    if (warningCount) handleInfo(`匯出完成，已嵌入 ${imageCount} 張圖片；${warningCount} 個圖片欄位未能嵌入，原因已於畫面列出。`);
    else handleSuccess(`匯出完成，已嵌入 ${imageCount} 張圖片。`);
    } catch (error) { handleError(error, '含圖 Excel 匯出'); }
    finally { setExportStatus(''); }
  };

  const handleDownloadTemplate = () => {
    const template = isSimpleProductCountry(country) ? [{
      圖片: '',
      名稱: '',
      料號: '',
      樣式: '',
      樣式圖片: '',
      商品相簿: 'https://example.com/a.jpg,https://example.com/b.jpg',
    }] : [{
      圖片: '',
      名稱: '',
      料號: '',
      直播價: '',
      商成價: '',
      贈品模式: '',
      備註: '',
      成本韓幣: '',
      成本日幣: '',
      成本台幣: '',
      成本泰銖: '',
      重量: '',
      樣式: '',
      樣式條碼: '',
      樣式圖片: '',
      商品相簿: '',
      子商品: '',
      贈品: '',
      廠商: ''
    }];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, '批次新增商品範本.xlsx');
  };

  const handleRebuildSearchIndex = async () => {
    setIsRebuildingIndex(true);
    setIndexRebuildStatus('正在重建商品搜尋索引...');

    try {
      const rebuildProductSearchIndex = httpsCallable(functions, 'rebuildProductSearchIndex');
      const response = await rebuildProductSearchIndex({ country });
      const indexedCount = response.data?.indexedCount || 0;
      const deletedCount = response.data?.deletedCount || 0;
      const message = `商品搜尋索引更新完成：已重建 ${indexedCount} 筆，清除舊索引 ${deletedCount} 筆`;
      setIndexRebuildStatus(message);
      handleSuccess(message);
    } catch (error) {
      console.error('重建商品搜尋索引失敗:', error);
      const message = '商品搜尋索引更新失敗，請稍後再試';
      setIndexRebuildStatus(message);
      handleError(error, '重建商品搜尋索引');
    } finally {
      setIsRebuildingIndex(false);
    }
  };

  const handleRebuildImageIndex = async () => {
    setIsRebuildingImageIndex(true);
    setImageIndexRebuildStatus('正在重建商品圖片索引...');

    try {
      const rebuildProductImageIndex = httpsCallable(functions, 'rebuildProductImageIndex');
      let cursor = null;
      let entryIndex = 0;
      let done = false;
      let totalIndexed = 0;
      let totalSkipped = 0;
      let totalDeleted = 0;
      let round = 0;

      while (!done) {
        round += 1;
        setImageIndexRebuildStatus(`正在重建商品圖片索引...第 ${round} 批`);
        const response = await rebuildProductImageIndex({
          country,
          cursor,
          entryIndex,
          clearExisting: round === 1,
          maxProducts: 40,
          batchSize: 1,
        });
        const data = response.data || {};
        totalIndexed += data.indexedCount || 0;
        totalSkipped += data.skippedCount || 0;
        totalDeleted += data.deletedCount || 0;
        cursor = data.cursor || null;
        entryIndex = data.entryIndex || 0;
        done = Boolean(data.done);
      }

      const message = `商品圖片索引更新完成：已建立 ${totalIndexed} 筆，略過 ${totalSkipped} 筆無圖片商品，清除舊索引 ${totalDeleted} 筆`;
      setImageIndexRebuildStatus(message);
      handleSuccess(message);
    } catch (error) {
      console.error('重建商品圖片索引失敗:', error);
      const message = '商品圖片索引更新失敗，請稍後再試';
      setImageIndexRebuildStatus(message);
      handleError(error, '重建商品圖片索引');
    } finally {
      setIsRebuildingImageIndex(false);
    }
  };

  // 列出所有相同料號的商品組供手動選擇
  const handleListSameItemNumberProducts = async () => {
    const productsRef = collection(db, collectionName);
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    const groups = {};
    products.forEach(product => {
      const itemNumber = product.itemNumber;
      if (itemNumber && itemNumber.trim() !== '') {
        if (!groups[itemNumber]) groups[itemNumber] = [];
        groups[itemNumber].push(product);
      }
    });
    // 只保留有2個以上相同料號的商品組
    const groupsArray = Object.entries(groups)
      .filter(([itemNumber, group]) => group.length >= 2)
      .map(([itemNumber, group]) => ({
        itemNumber,
        products: group
      }));
    
    setSameItemNumberGroups(groupsArray);
    setSelectedProducts({});
    setShowMergeSelection(true);
  };

  // 切換商品選中狀態
  const toggleProductSelection = (productId) => {
    const newSelected = { ...selectedProducts };
    if (newSelected[productId]) {
      delete newSelected[productId];
    } else {
      newSelected[productId] = true;
    }
    setSelectedProducts(newSelected);
  };

  // 手動合併選中的商品（顯示預覽）
  const handleManualMergePreview = async () => {
    const selectedCount = Object.keys(selectedProducts).length;
    if (selectedCount < 2) {
      handleWarning('請至少選擇2個商品進行合併');
      return;
    }

    const productsRef = collection(
      db,
      getCollectionName(country)
    );

    const selectedProductsArray = Object.keys(selectedProducts).filter(key => selectedProducts[key]);
    const productsToMerge = [];
    
    // 獲取選中的商品資料
    for (const productId of selectedProductsArray) {
      const productDoc = await getDoc(doc(productsRef, productId));
      if (productDoc.exists()) {
        productsToMerge.push({ id: productId, ...productDoc.data() });
      }
    }

    if (productsToMerge.length < 2) {
      handleError(new Error('無法獲取選中的商品資料'), '手動合併');
      return;
    }

    const previewGroups = generateMergePreview(productsToMerge, 'manual');
    
    if (previewGroups.length === 0) {
      handleInfo('沒有可合併的商品（每個料號組至少需要2個商品）');
      return;
    }

    setMergePreview({ groups: previewGroups, type: 'manual' });
  };

  // 確認並執行合併
  const handleConfirmMerge = async () => {
    if (!mergePreview) return;

    try {
      const { totalMerged, mergedGroups } = await executeMerge(mergePreview.groups, mergePreview.type);
      
      if (totalMerged > 0) {
        handleSuccess(`合併完成！共合併了 ${totalMerged} 個商品（${mergedGroups} 個料號組）`);
      } else {
        handleInfo('沒有可合併的商品');
      }
      
      setMergePreview(null);
      if (mergePreview.type === 'manual') {
        setShowMergeSelection(false);
        setSelectedProducts(new Set());
      }
      // 觸發重新載入數據
      setRefreshKey(prev => prev + 1);
      // 如果是在手動合併模式下，重新載入相同料號的商品組
      if (mergePreview.type === 'manual') {
        handleListSameItemNumberProducts();
      }
    } catch (error) {
      handleError(error, '合併商品');
    }
  };

  // 生成合併預覽
  const generateMergePreview = (products, type = 'auto') => {
    const groups = {};
    products.forEach(product => {
      const itemNumber = product.itemNumber;
      if (itemNumber) {
        if (!groups[itemNumber]) groups[itemNumber] = [];
        groups[itemNumber].push(product);
      }
    });

    const previewGroups = [];
    for (const itemNumber in groups) {
      const group = groups[itemNumber];
      if (group.length < 2) continue;
      
      // 選擇主商品的優先順序
      let mainProduct = group.find(prod => prod.weight && prod.weight.trim() !== '' && prod.weight !== '0');
      if (!mainProduct) {
        mainProduct = group.find(prod => 
          (prod.costTWD && prod.costTWD.trim() !== '') ||
          (prod.costKoreanWon && prod.costKoreanWon.trim() !== '') ||
          (prod.costJPY && prod.costJPY.trim() !== '') ||
          (prod.costTHB && prod.costTHB.trim() !== '')
        );
      }
      if (!mainProduct) {
        mainProduct = group[0];
      }
      
      const mergeCandidates = group.filter(prod => prod.id !== mainProduct.id);
      if (mergeCandidates.length === 0) continue;

      // 計算合併後的樣式
      let updatedStyles = mainProduct.styles ? [...mainProduct.styles] : [];
      let updatedStyleBarcodes = mainProduct.styleBarcodes ? [...mainProduct.styleBarcodes] : [];
      let updatedRawStyleBarcodes = mainProduct.rawStyleBarcodes ? [...mainProduct.rawStyleBarcodes] : [];
      let updatedStyleDetails = mainProduct.styleDetails ? [...mainProduct.styleDetails] : [];
      
      const newStyles = [];
      const newBarcodes = [];
      const newRawBarcodes = [];

      for (const candidate of mergeCandidates) {
        if (candidate.styles && candidate.styleBarcodes) {
          candidate.styles.forEach((style, index) => {
            const combinedBarcode = candidate.styleBarcodes[index];
            const rawBarcode = candidate.rawStyleBarcodes?.[index] || extractRawBarcode(combinedBarcode, itemNumber);
            const styleDetail = candidate.styleDetails?.[index];
            
            if (!updatedStyleBarcodes.includes(combinedBarcode)) {
              updatedStyles.push(style);
              updatedStyleBarcodes.push(combinedBarcode);
              updatedRawStyleBarcodes.push(rawBarcode);
              // 合併樣式詳細信息
              updatedStyleDetails.push(styleDetail || null);
              newStyles.push(style);
              newBarcodes.push(rawBarcode);
              newRawBarcodes.push(rawBarcode);
            }
          });
        }
      }

      previewGroups.push({
        itemNumber,
        mainProduct,
        mergeCandidates,
        currentStyles: mainProduct.styles || [],
        newStyles,
        finalStyles: updatedStyles,
        currentBarcodes: mainProduct.rawStyleBarcodes || [],
        newBarcodes,
        finalBarcodes: updatedRawStyleBarcodes,
        finalStyleDetails: updatedStyleDetails
      });
    }

    return previewGroups;
  };

  const getProductRawBarcodes = (product) => {
    const rawStyleBarcodes = Array.isArray(product.rawStyleBarcodes) ? product.rawStyleBarcodes : [];
    const styleBarcodes = Array.isArray(product.styleBarcodes) ? product.styleBarcodes : [];
    const itemNumber = product.itemNumber || '';

    return styleBarcodes
      .map((barcode, index) => {
        const rawBarcode = rawStyleBarcodes[index] || extractRawBarcode(barcode, itemNumber);
        return String(rawBarcode || '').trim();
      })
      .filter(Boolean);
  };

  const generateBarcodeMergePreview = (products) => {
    const groups = {};
    const usedProductIds = {};

    products.forEach(product => {
      // 組合商品不參與相同條碼自動合併，避免拆錯組合包。
      if (product.subProducts && product.subProducts.length > 0) return;

      const rawBarcodes = getProductRawBarcodes(product);
      rawBarcodes.forEach(rawBarcode => {
        if (!groups[rawBarcode]) groups[rawBarcode] = [];
        groups[rawBarcode].push(product);
      });
    });

    const previewGroups = [];
    Object.entries(groups).forEach(([rawBarcode, group]) => {
      const uniqueProducts = group.filter((product, index, array) =>
        array.findIndex(item => item.id === product.id) === index
      );

      if (uniqueProducts.length < 2) return;
      if (uniqueProducts.some(product => usedProductIds[product.id])) return;
      // 同一條碼若同時指向不同料號，交由人工確認，避免誤刪有效商品。
      if (hasConflictingItemNumbers(uniqueProducts)) return;

      const mainProduct = chooseMainProductForMerge(uniqueProducts);
      const mergeCandidates = uniqueProducts.filter(prod => prod.id !== mainProduct.id);
      if (mergeCandidates.length === 0) return;

      let updatedStyles = mainProduct.styles ? [...mainProduct.styles] : [];
      let updatedRawStyleBarcodes = getProductRawBarcodes(mainProduct);
      let updatedStyleDetails = mainProduct.styleDetails ? [...mainProduct.styleDetails] : [];
      const newStyles = [];
      const newBarcodes = [];

      mergeCandidates.forEach(candidate => {
        const candidateRawBarcodes = getProductRawBarcodes(candidate);
        const candidateStyles = candidate.styles || [];

        candidateRawBarcodes.forEach((candidateBarcode, index) => {
          if (!updatedRawStyleBarcodes.includes(candidateBarcode)) {
            const candidateStyle = candidateStyles[index] || '無樣式';
            updatedStyles.push(candidateStyle);
            updatedRawStyleBarcodes.push(candidateBarcode);
            updatedStyleDetails.push(candidate.styleDetails?.[index] || null);
            newStyles.push(candidateStyle);
            newBarcodes.push(candidateBarcode);
          }
        });
      });

      uniqueProducts.forEach(product => {
        usedProductIds[product.id] = true;
      });

      previewGroups.push({
        itemNumber: mainProduct.itemNumber || '',
        mergeKey: rawBarcode,
        mainProduct,
        mergeCandidates,
        currentStyles: mainProduct.styles || [],
        newStyles,
        finalStyles: updatedStyles,
        currentBarcodes: getProductRawBarcodes(mainProduct),
        newBarcodes,
        finalBarcodes: updatedRawStyleBarcodes,
        finalStyleDetails: updatedStyleDetails
      });
    });

    return previewGroups;
  };

  // 執行實際合併
  const executeMerge = async (previewGroups, type = 'auto') => {

    let totalMerged = 0;
    let mergedGroups = 0;

    for (const preview of previewGroups) {
      const { mainProduct, mergeCandidates, finalStyles, finalBarcodes, finalStyleDetails, itemNumber } = preview;
      
      // 組合條碼
      const combinedBarcodes = finalBarcodes.map(rawBarcode => 
        combineBarcode(rawBarcode, itemNumber)
      );

      const mergedBaseProduct = fillMissingProductFields(mainProduct, mergeCandidates);
      let updatedHistory = mainProduct.history ? [...mainProduct.history] : [];
      for (const candidate of mergeCandidates) {
        if (candidate.history?.length > 0) {
          updatedHistory.push(...candidate.history);
        }
        updatedHistory.push({
          date: new Date().toLocaleDateString(),
          mergedProductId: candidate.id,
          action: type === 'manual' ? '手動合併' : type === 'barcode' ? '相同條碼自動合併' : '自動合併',
          itemNumber: itemNumber,
          mergeKey: preview.mergeKey || ''
        });
        totalMerged++;
      }

      const updatedData = {
        ...mergedBaseProduct,
        styles: finalStyles,
        styleBarcodes: combinedBarcodes,
        rawStyleBarcodes: finalBarcodes,
        // 只有在有樣式詳細信息時才設置，否則不設置（讓 cleanDataForFirestore 移除）
        styleDetails: finalStyleDetails || [],
        history: updatedHistory
      };
      // 清理資料，移除所有 undefined 值
      const cleanedData = cleanDataForFirestore(updatedData);
      await mergeLegacyProductsSafely({ country, mainProduct, candidates: mergeCandidates, product: cleanedData });
      mergedGroups++;
    }

    return { totalMerged, mergedGroups };
  };

  // 自動合併功能（顯示預覽）
  const handleMergeProducts = async () => {
    const productsRef = collection(
      db,
      getCollectionName(country)
    );
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    
    const previewGroups = generateMergePreview(products, 'auto');
    
    if (previewGroups.length === 0) {
      handleInfo('沒有可合併的商品（每個料號組至少需要2個商品）');
      return;
    }

    setMergePreview({ groups: previewGroups, type: 'auto' });
  };

  const handleMergeProductsByBarcode = async () => {
    const productsRef = collection(
      db,
      getCollectionName(country)
    );
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    const previewGroups = generateBarcodeMergePreview(products);

    if (previewGroups.length === 0) {
      handleInfo('沒有可合併的相同條碼商品');
      return;
    }

    setMergePreview({ groups: previewGroups, type: 'barcode' });
  };

  // 匯出異常商品
  const handleExportAbnormalProducts = async () => {
    if (!abnormalProducts || abnormalProducts.products.length === 0) {
      handleInfo('沒有異常商品可匯出');
      return;
    }

    try {
    const exportRates = await readExportRates();
    const exportData = abnormalProducts.products.map(product => ({
      商品名稱: product.name || '無名稱',
      料號: product.itemNumber || '',
      商品ID: product.id,
      圖片: product.image || '',
      直播價: product.livePrice || '',
      商成價: product.onlinePrice || '',
      備註: product.remarks || '',
      成本韓幣: product.costKoreanWon || '',
      成本日幣: product.costJPY || '',
      成本台幣: product.costTWD || '',
      成本泰銖: product.costTHB || '',
      ...koreanExportCosts(product, {}, exportRates),
      重量: productExportWeight(product).value,
      廠商: product.vendor || '',
      樣式: product.styles ? product.styles.join(', ') : '',
      樣式條碼: product.rawStyleBarcodes ? product.rawStyleBarcodes.join(', ') : ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '異常商品');
    
    const fileName = `異常商品檢查結果_${country}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    handleSuccess(`已匯出 ${abnormalProducts.products.length} 筆異常商品資料`);
    } catch (error) { handleError(error, '匯出異常商品'); }
  };

  // 生成4位數驗證碼
  const generateVerificationCode = () => {
    // 使用 Date.now() 的毫秒數來生成偽隨機數，避免使用 Math.random
    const timestamp = Date.now();
    const seed = timestamp % 10000;
    const randomValue = (seed / 10000); // 0 到 0.9999
    const calculatedValue = 1000 + randomValue * 9000;
    const floorValue = calculatedValue | 0;
    return floorValue.toString();
  };

  // 查詢料號對應的商品及其樣式
  const handleSearchProductsByItemNumbers = async () => {
    if (!itemNumbersInput.trim()) {
      handleWarning('請輸入料號');
      return;
    }

    try {
      const itemNumbers = itemNumbersInput
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (itemNumbers.length === 0) {
        handleWarning('請輸入至少一個料號');
        return;
      }

      const productsRef = collection(
        db,
          getCollectionName(country)
      );

      const products = [];
      for (const itemNumber of itemNumbers) {
        const q = query(productsRef, where('itemNumber', '==', itemNumber));
        const snapshot = await getDocs(q);
        snapshot.forEach(doc => {
          const productData = doc.data();
          if (productData.styles && productData.styles.length > 0) {
            products.push({
              id: doc.id,
              itemNumber: itemNumber,
              name: productData.name || '無名稱',
              styles: productData.styles || [],
              styleBarcodes: productData.styleBarcodes || [],
              rawStyleBarcodes: productData.rawStyleBarcodes || [],
              styleDetails: productData.styleDetails || []
            });
            }
          });
        }

      if (products.length === 0) {
        handleInfo('沒有找到有樣式的商品');
        return;
      }

      setProductsWithStyles(products);
      setSelectedStylesToRemove({});
      // 生成全部移除的驗證碼
      setRemoveAllVerificationCode(generateVerificationCode());
      setRemoveAllInputCode('');
    } catch (error) {
      handleError(error, '查詢商品樣式');
    }
  };

  // 切換樣式選中狀態
  const toggleStyleSelection = (productId, styleIndex) => {
    const key = `${productId}-${styleIndex}`;
    const newSelected = { ...selectedStylesToRemove };
    if (newSelected[key]) {
      delete newSelected[key];
    } else {
      newSelected[key] = true;
    }
    setSelectedStylesToRemove(newSelected);
  };

  // 根據條碼關鍵字搜尋並選中樣式
  const handleSearchStylesByBarcode = async () => {
    if (!barcodeKeyword.trim()) {
      handleWarning('請輸入條碼關鍵字');
      return;
    }

    try {
      const productsRef = collection(
        db,
        getCollectionName(country)
      );

      const snapshot = await getDocs(productsRef);
      // 使用普通對象代替 Map 以避免初始化問題
      const matchedStyles = {};
      const matchedProducts = [];
      const keyword = barcodeKeyword.trim().toUpperCase();

      snapshot.forEach(doc => {
        const productData = doc.data();
        const productId = doc.id;
        
        // 跳過組合商品（有子商品的商品）
        if (productData.subProducts && productData.subProducts.length > 0) {
          return;
        }
        
        const styleBarcodes = productData.styleBarcodes || [];
        const rawStyleBarcodes = productData.rawStyleBarcodes || [];
        let hasMatchedStyle = false;

        // 檢查每個樣式的條碼（組合條碼和原始條碼）
        if (productData.styles && productData.styles.length > 0) {
          productData.styles.forEach((style, index) => {
            const combinedBarcode = styleBarcodes[index] || '';
            const rawBarcode = rawStyleBarcodes[index] || '';
            
            // 檢查條碼是否包含關鍵字（不區分大小寫）
            if (combinedBarcode.toUpperCase().includes(keyword) || 
                rawBarcode.toUpperCase().includes(keyword)) {
              const key = `${productId}-${index}`;
              matchedStyles[key] = true;
              hasMatchedStyle = true;
            }
          });
        }

        // 如果有匹配的樣式，將商品加入列表以便顯示
        if (hasMatchedStyle && productData.styles && productData.styles.length > 0) {
          matchedProducts.push({
            id: productId,
            itemNumber: productData.itemNumber || '',
            name: productData.name || '無名稱',
            styles: productData.styles || [],
            styleBarcodes: productData.styleBarcodes || [],
            rawStyleBarcodes: productData.rawStyleBarcodes || [],
            styleDetails: productData.styleDetails || []
          });
        }
      });

      const matchedCount = Object.keys(matchedStyles).length;
      if (matchedCount === 0) {
        handleInfo('沒有找到符合條碼關鍵字的樣式');
        return;
      }

      // 更新商品列表和選中的樣式
      setProductsWithStyles(matchedProducts);
      setSelectedStylesToRemove(matchedStyles);
      handleSuccess(`找到 ${matchedCount} 個符合條碼關鍵字的樣式，已自動選中`);
    } catch (error) {
      handleError(error, '搜尋條碼關鍵字');
    }
  };

  // 移除選中的樣式
  const handleRemoveSelectedStyles = async () => {
    const selectedCount = Object.keys(selectedStylesToRemove).length;
    if (selectedCount === 0) {
      handleWarning('請至少選擇一個要移除的樣式');
      return;
    }

    try {
      const productsRef = collection(
        db,
          getCollectionName(country)
      );

      // 按商品分組要移除的樣式（使用普通對象代替 Map）
      const updatesByProduct = {};
      Object.keys(selectedStylesToRemove).forEach((key) => {
        const [productId, styleIndex] = key.split('-');
        const index = parseInt(styleIndex);
        if (!updatesByProduct[productId]) {
          updatesByProduct[productId] = [];
        }
        updatesByProduct[productId].push(index);
      });

      let removedCount = 0;
      for (const productId in updatesByProduct) {
        const indices = updatesByProduct[productId];
        const productDoc = await getDoc(doc(productsRef, productId));
        if (!productDoc.exists()) continue;

        const productData = productDoc.data();
        const styles = [...(productData.styles || [])];
        const styleBarcodes = [...(productData.styleBarcodes || [])];
        const rawStyleBarcodes = [...(productData.rawStyleBarcodes || [])];
        const styleDetails = [...(productData.styleDetails || [])];

        // 確保所有陣列長度一致（以 styles 為準）
        const lengths = [styles.length, styleBarcodes.length, rawStyleBarcodes.length, styleDetails.length];
        let maxLength = 0;
        lengths.forEach(len => {
          if (len > maxLength) maxLength = len;
        });
        while (styleBarcodes.length < maxLength) styleBarcodes.push('');
        while (rawStyleBarcodes.length < maxLength) rawStyleBarcodes.push('');
        while (styleDetails.length < maxLength) styleDetails.push(null);
        
        // 從大到小排序索引，避免刪除時索引錯亂
        const sortedIndices = [...indices].sort((a, b) => b - a);
        
        // 從後往前移除，確保索引正確
        sortedIndices.forEach(index => {
          if (index >= 0 && index < styles.length) {
            // 確保所有相關陣列都同步移除（樣式、條碼、樣式詳細信息）
            styles.splice(index, 1);
            if (index < styleBarcodes.length) styleBarcodes.splice(index, 1);
            if (index < rawStyleBarcodes.length) rawStyleBarcodes.splice(index, 1);
            if (index < styleDetails.length) styleDetails.splice(index, 1);
            removedCount++;
          }
        });

        // 更新商品資料
        const updatedData = {
          ...productData,
          styles,
          styleBarcodes,
          rawStyleBarcodes,
          // 只有在有樣式詳細信息時才設置，否則不設置（讓 cleanDataForFirestore 移除）
          styleDetails,
          history: [
            ...(productData.history || []),
            {
          date: new Date().toLocaleDateString(),
              action: '移除樣式',
              details: `移除了 ${sortedIndices.length} 個樣式`
            }
          ]
        };

        // 清理資料，移除所有 undefined 值
        const cleanedData = cleanDataForFirestore(updatedData);
        await setDoc(doc(productsRef, productId), cleanedData, productData);
      }

      handleSuccess(`成功移除 ${removedCount} 個樣式`);
      // 重新查詢商品
      await handleSearchProductsByItemNumbers();
      setSelectedStylesToRemove({});
    } catch (error) {
      handleError(error, '移除樣式');
    }
  };

  // 全部移除樣式
  const handleRemoveAllStyles = async () => {
    if (removeAllInputCode !== removeAllVerificationCode) {
      handleWarning('驗證碼錯誤，請重新輸入！');
      const newCode = generateVerificationCode();
      setRemoveAllVerificationCode(newCode);
      setRemoveAllInputCode('');
      return;
    }

    if (productsWithStyles.length === 0) {
      handleWarning('沒有可移除的商品');
      return;
    }

    try {
      const productsRef = collection(
        db,
          getCollectionName(country)
      );

      let removedCount = 0;
      for (const product of productsWithStyles) {
        const productDoc = await getDoc(doc(productsRef, product.id));
        if (!productDoc.exists()) continue;

        const productData = productDoc.data();
        const styleCount = (productData.styles || []).length;

        if (styleCount > 0) {
          const updatedData = {
            ...productData,
            styles: [],
            styleBarcodes: [],
            rawStyleBarcodes: [],
            styleDetails: [], // Explicitly clear styles without deleting unrelated legacy fields.
            history: [
              ...(productData.history || []),
              {
                date: new Date().toLocaleDateString(),
                action: '移除所有樣式',
                details: `移除了所有 ${styleCount} 個樣式`
              }
            ]
          };

          // 清理資料，移除所有 undefined 值和 styleDetails 欄位（如果為空）
          const cleanedData = cleanDataForFirestore(updatedData);
          await setDoc(doc(productsRef, product.id), cleanedData, productData);
          removedCount += styleCount;
        }
      }

      handleSuccess(`成功移除所有樣式，共 ${removedCount} 個`);
      // 重新查詢商品
      await handleSearchProductsByItemNumbers();
      setSelectedStylesToRemove({});
      setRemoveAllInputCode('');
      setRemoveAllVerificationCode(generateVerificationCode());
    } catch (error) {
      handleError(error, '全部移除樣式');
    }
  };

  // 移除空白條碼的樣式
  const handleRemoveEmptyBarcodeStyles = async () => {
    try {
      const productsRef = collection(
        db,
        getCollectionName(country)
      );

      const snapshot = await getDocs(productsRef);
      const productsToUpdate = [];
      let totalRemovedCount = 0;

      snapshot.forEach(doc => {
        const productData = doc.data();
        
        // 跳過組合商品（有子商品的商品）
        if (productData.subProducts && productData.subProducts.length > 0) {
          return;
        }
        
        const styles = [...(productData.styles || [])];
        const styleBarcodes = [...(productData.styleBarcodes || [])];
        const rawStyleBarcodes = [...(productData.rawStyleBarcodes || [])];
        const styleDetails = [...(productData.styleDetails || [])];

        // 確保所有陣列長度一致
        const lengths = [styles.length, styleBarcodes.length, rawStyleBarcodes.length, styleDetails.length];
        let maxLength = 0;
        lengths.forEach(len => {
          if (len > maxLength) maxLength = len;
        });
        while (styleBarcodes.length < maxLength) styleBarcodes.push('');
        while (rawStyleBarcodes.length < maxLength) rawStyleBarcodes.push('');
        while (styleDetails.length < maxLength) styleDetails.push(null);

        // 找出所有空白條碼的樣式索引（從後往前，避免索引錯亂）
        const indicesToRemove = [];
        for (let i = styles.length - 1; i >= 0; i--) {
          const rawBarcode = rawStyleBarcodes[i] || '';
          const combinedBarcode = styleBarcodes[i] || '';
          // 如果原始條碼和組合條碼都為空，則標記為移除
          if (!rawBarcode.trim() && !combinedBarcode.trim()) {
            indicesToRemove.push(i);
          }
        }

        if (indicesToRemove.length > 0) {
          // 移除空白條碼的樣式（從後往前移除，避免索引錯亂）
          indicesToRemove.sort((a, b) => b - a).forEach(index => {
            if (index >= 0 && index < styles.length) {
              styles.splice(index, 1);
              if (index < styleBarcodes.length) styleBarcodes.splice(index, 1);
              if (index < rawStyleBarcodes.length) rawStyleBarcodes.splice(index, 1);
              if (index < styleDetails.length) styleDetails.splice(index, 1);
            }
          });

          productsToUpdate.push({
            id: doc.id,
            productData,
            styles,
            styleBarcodes,
            rawStyleBarcodes,
            styleDetails,
            removedCount: indicesToRemove.length
          });
          totalRemovedCount += indicesToRemove.length;
        }
      });

      if (productsToUpdate.length === 0) {
        handleInfo('沒有找到空白條碼的樣式');
        return;
      }

      // 更新所有商品
      for (const update of productsToUpdate) {
      const updatedData = {
          ...update.productData,
          styles: update.styles,
          styleBarcodes: update.styleBarcodes,
          rawStyleBarcodes: update.rawStyleBarcodes,
          styleDetails: update.styleDetails,
          history: [
            ...(update.productData.history || []),
            {
              date: new Date().toLocaleDateString(),
              action: '移除空白條碼樣式',
              details: `移除了 ${update.removedCount} 個空白條碼的樣式`
            }
          ]
        };

        const cleanedData = cleanDataForFirestore(updatedData);
        await setDoc(doc(productsRef, update.id), cleanedData, update.productData);
      }

      handleSuccess(`成功移除 ${totalRemovedCount} 個空白條碼的樣式，共影響 ${productsToUpdate.length} 個商品`);
    } catch (error) {
      handleError(error, '移除空白條碼樣式');
    }
  };

  // 移除空白條碼商品樣式（移除整個商品，如果商品的所有樣式都沒有條碼，排除組合商品）
  const handleRemoveEmptyBarcodeProducts = async () => {
    try {
      const productsRef = collection(
        db,
        getCollectionName(country)
      );

      const snapshot = await getDocs(productsRef);
      const productsToDelete = [];
      let totalDeletedCount = 0;

      snapshot.forEach(doc => {
        const productData = doc.data();
        
        // 跳過組合商品（有子商品的商品）
        if (productData.subProducts && productData.subProducts.length > 0) {
          return;
        }
        
        const styles = productData.styles || [];
        const styleBarcodes = productData.styleBarcodes || [];
        const rawStyleBarcodes = productData.rawStyleBarcodes || [];

        // 如果商品沒有樣式，跳過
        if (styles.length === 0) {
          return;
        }

        // 檢查是否所有樣式都沒有條碼
        let allStylesEmpty = true;
        for (let i = 0; i < styles.length; i++) {
          const rawBarcode = rawStyleBarcodes[i] || '';
          const combinedBarcode = styleBarcodes[i] || '';
          // 如果至少有一個樣式有條碼，就不是空白條碼商品
          if (rawBarcode.trim() || combinedBarcode.trim()) {
            allStylesEmpty = false;
            break;
          }
        }

        // 如果所有樣式都沒有條碼，標記為刪除
        if (allStylesEmpty) {
          productsToDelete.push({
            id: doc.id,
            name: productData.name || '無名稱',
            itemNumber: productData.itemNumber || 'N/A'
          });
          totalDeletedCount++;
        }
      });

      if (productsToDelete.length === 0) {
        handleInfo('沒有找到空白條碼的商品');
        return;
      }

      // 確認刪除
      const confirmMessage = `找到 ${productsToDelete.length} 個空白條碼的商品，確定要刪除嗎？\n\n商品列表：\n${productsToDelete.slice(0, 10).map(p => `- ${p.name} (${p.itemNumber})`).join('\n')}${productsToDelete.length > 10 ? `\n... 還有 ${productsToDelete.length - 10} 個商品` : ''}`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // 刪除所有空白條碼商品
      for (const product of productsToDelete) {
        await deleteDoc(doc(productsRef, product.id));
      }

      handleSuccess(`成功刪除 ${totalDeletedCount} 個空白條碼的商品`);
    } catch (error) {
      handleError(error, '移除空白條碼商品');
    }
  };

  // 檢查異常商品（料號不在品名中）
  const handleCheckAbnormalProducts = async () => {
    try {
      const productsRef = collection(
        db,
          getCollectionName(country)
      );
      const snapshot = await getDocs(productsRef);
      const products = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      
      const abnormalList = [];
      
      products.forEach(product => {
        const itemNumber = product.itemNumber;
        const name = product.name || '';
        
        // 如果沒有料號或沒有品名，跳過
        if (!itemNumber || !name || itemNumber.trim() === '' || name.trim() === '') {
          return;
        }
        
        // 檢查料號是否在品名中（不區分大小寫）
        // 如果料號後面有「-」，要將「-」加在料號後面來比對品名
        const itemNumberUpper = itemNumber.trim().toUpperCase();
        const nameUpper = name.trim().toUpperCase();
        
        // 先檢查原始料號是否在品名中
        let isFound = nameUpper.includes(itemNumberUpper);
        
        // 如果沒找到，且料號後面沒有「-」，嘗試在料號後面加上「-」來比對
        if (!isFound && !itemNumberUpper.endsWith('-')) {
          // 嘗試多種格式：料號 + 「-」、料號 + 「 - 」（帶空白）
          const variations = [
            itemNumberUpper + '-',      // KA1234-
            itemNumberUpper + ' - ',    // KA1234 - 
            itemNumberUpper + ' -',     // KA1234 -
            itemNumberUpper + '- ',     // KA1234- 
          ];
          
          // 檢查是否有任何變體在品名中
          isFound = variations.some(variation => nameUpper.includes(variation));
        }
        
        if (!isFound) {
          abnormalList.push({
            id: product.id,
            name: name,
            itemNumber: itemNumber,
            ...product
          });
        }
      });
      
      setAbnormalProducts({
        products: abnormalList,
        show: true,
        total: products.length,
        abnormalCount: abnormalList.length
      });
    } catch (error) {
      handleError(error, '異常商品檢查');
    }
  };

  // 合併相同樣式的功能
  const handleMergeSameStyles = async () => {
    const productsRef = collection(
      db,
      getCollectionName(country)
    );
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    
    let totalMerged = 0;
    
    // 對每個商品進行樣式合併
    for (const product of products) {
      if (!product.styles || !product.styleBarcodes || product.styles.length <= 1) {
        continue;
      }
      
      let styles = [...product.styles];
      let styleBarcodes = [...product.styleBarcodes];
      let rawStyleBarcodes = product.rawStyleBarcodes ? [...product.rawStyleBarcodes] : [];
      let styleDetails = product.styleDetails ? [...product.styleDetails] : [];
      let hasChanges = false;
      
      // 如果沒有 rawStyleBarcodes，從 styleBarcodes 中提取
      if (rawStyleBarcodes.length === 0 && styleBarcodes.length > 0) {
        rawStyleBarcodes = styleBarcodes.map(barcode => 
          extractRawBarcode(barcode, product.itemNumber || '')
        );
      }
      
      // 確保 styleDetails 長度與 styles 一致
      while (styleDetails.length < styles.length) {
        styleDetails.push(null);
      }
      
      // 檢查每個樣式是否有需要合併的情況
      for (let i = 0; i < styles.length; i++) {
        for (let j = i + 1; j < styles.length; j++) {
          const style1 = styles[i];
          const style2 = styles[j];
          
          // 跳過已經被標記為刪除的樣式
          if (style1 === null || style2 === null) continue;
          
          // 檢查是否為相同樣式但開頭不同（一個數字開頭，一個英文開頭）
          const style1WithoutPrefix = style1.replace(/^[0-9]+|^[a-zA-Z]+/, '');
          const style2WithoutPrefix = style2.replace(/^[0-9]+|^[a-zA-Z]+/, '');
          
          // 如果去除前綴後相同，且一個是數字開頭一個是英文開頭
          if (style1WithoutPrefix === style2WithoutPrefix && style1WithoutPrefix !== '') {
            const style1StartsWithNumber = /^[0-9]/.test(style1);
            const style1StartsWithLetter = /^[a-zA-Z]/.test(style1);
            const style2StartsWithNumber = /^[0-9]/.test(style2);
            const style2StartsWithLetter = /^[a-zA-Z]/.test(style2);
            
            // 一個數字開頭，一個英文開頭的情況
            if ((style1StartsWithNumber && style2StartsWithLetter) || 
                (style1StartsWithLetter && style2StartsWithNumber)) {
              
              // 保留數字開頭的，刪除英文開頭的
              if (style1StartsWithNumber && style2StartsWithLetter) {
                // 保留 style1，刪除 style2
                styles[j] = null;
                styleBarcodes[j] = null;
                rawStyleBarcodes[j] = null;
                if (styleDetails.length > j) styleDetails[j] = null;
                hasChanges = true;
                totalMerged++;
              } else if (style2StartsWithNumber && style1StartsWithLetter) {
                // 保留 style2，刪除 style1
                styles[i] = null;
                styleBarcodes[i] = null;
                rawStyleBarcodes[i] = null;
                if (styleDetails.length > i) styleDetails[i] = null;
                hasChanges = true;
                totalMerged++;
              }
            }
          }
        }
      }
      
      // 如果有變化，更新商品資料
      if (hasChanges) {
        // 過濾掉被標記為null的項目
        const filteredStyles = styles.filter(style => style !== null);
        const filteredStyleBarcodes = styleBarcodes.filter(barcode => barcode !== null);
        const filteredRawStyleBarcodes = rawStyleBarcodes.filter(barcode => barcode !== null);
        const filteredStyleDetails = styleDetails.filter((detail, index) => styles[index] !== null);
        
        const updatedData = {
          ...product,
          styles: filteredStyles,
          styleBarcodes: filteredStyleBarcodes, // 組合條碼
          rawStyleBarcodes: filteredRawStyleBarcodes, // 原始條碼
          // 只有在有樣式詳細信息時才設置，否則不設置（讓 cleanDataForFirestore 移除）
          styleDetails: filteredStyleDetails,
          history: [
            ...(product.history || []),
            {
              date: new Date().toLocaleDateString(),
              action: '樣式合併',
              details: '合併相同樣式（保留數字開頭）'
            }
          ]
        };
        
        // 清理資料，移除所有 undefined 值
        const cleanedData = cleanDataForFirestore(updatedData);
        await setDoc(doc(productsRef, product.id), cleanedData, product);
      }
    }
    
    handleSuccess(`樣式合併完成！共合併了 ${totalMerged} 個重複樣式`);
    // 觸發重新載入數據
    setRefreshKey(prev => prev + 1);
  };

  const pageStyle = {
    minHeight: '100vh',
    background: '#f8f9fa',
    padding: '1rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    maxWidth: '480px',
    margin: '0 auto'
  };

  const buttonStyle = {
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '0.75rem',
    transition: 'all 0.2s'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    boxSizing: 'border-box',
    marginBottom: '0.75rem'
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#333', textAlign: 'center' }}>批次新增商品</h2>
        <p role="status" style={{ padding: 12, background: '#fff7dc', color: '#765719' }}>可匯入尚未使用直播備貨的舊商品及新商品。上傳前會檢查整份匯入資料；若包含已使用穩定款式、功效或備貨資料的商品，或料號／條碼對應多筆商品，整份檔案會停止且不寫入。請依列號移除或修正後再上傳。受保護商品請使用商品編輯或直播備貨，不可在舊批次工具覆寫、刪除或合併。</p>
        <button type="button" onClick={() => navigate(`/prep/${country}`)}>前往直播備貨與功效匯入</button>
        <input 
          type="file" 
          onChange={handleFileChange} 
          style={inputStyle}
          disabled={isUploading}
          accept=".xlsx,.xls"
        />
        <button 
          onClick={handleFileUpload}
          disabled={isUploading || !file}
          style={{
            ...buttonStyle,
            opacity: (isUploading || !file) ? 0.6 : 1,
            cursor: (isUploading || !file) ? 'not-allowed' : 'pointer'
          }}
          onMouseOver={(e) => { if (!isUploading && file) e.target.style.background = '#555'; }}
          onMouseOut={(e) => { if (!isUploading && file) e.target.style.background = '#333'; }}
        >
          {isUploading ? '上傳中...' : '上傳'}
        </button>

        {/* 上傳進度顯示 */}
        {uploadProgress && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e5e5e5'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                {uploadProgress.status === 'reading' && '讀取檔案中...'}
                {uploadProgress.status === 'preflighting' && '檢查整份檔案與商品保護狀態中...'}
                {uploadProgress.status === 'uploading' && `上傳中: ${uploadProgress.current} / ${uploadProgress.total}`}
                {uploadProgress.status === 'completed' && '上傳完成'}
                {uploadProgress.status === 'error' && '上傳失敗'}
              </span>
              {uploadProgress.total > 0 && (
                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                  {uploadProgress.total > 0 ? ((uploadProgress.current / uploadProgress.total) * 100 | 0) : 0}%
                </span>
              )}
            </div>
            {uploadProgress.total > 0 && (
              <div style={{
                width: '100%',
                height: '20px',
                backgroundColor: '#e5e5e5',
                borderRadius: '10px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                  height: '100%',
                  backgroundColor: uploadProgress.status === 'error' ? '#dc3545' : '#28a745',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            )}
            {uploadProgress.successCount !== undefined && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                ✅ 成功: {uploadProgress.successCount} 筆
                {uploadProgress.errorCount > 0 && (
                  <span style={{ color: '#dc3545', marginLeft: '1rem' }}>
                    ❌ 失敗: {uploadProgress.errorCount} 筆
                  </span>
                )}
              </div>
            )}
            {uploadProgress.status === 'error' && uploadProgress.error && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '0.85rem', color: '#856404' }}>
                {uploadProgress.error}
              </div>
            )}
          </div>
        )}

        {/* 錯誤列表 */}
        {uploadErrors.length > 0 && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffc107',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', color: '#856404' }}>錯誤詳情 ({uploadErrors.length} 筆)</h4>
              <button
                onClick={() => setUploadErrors([])}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#ffc107',
                  color: '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                清除
              </button>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#856404' }}>
              {uploadErrors.map((error, index) => (
                <div key={index} style={{ marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px' }}>
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
        <button 
          onClick={handleExport} disabled={Boolean(exportStatus)}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          {exportStatus || '匯出所有商品資料（含圖片）'}
        </button>
        <button 
          onClick={handleDownloadTemplate}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          下載範本
        </button>
        <button 
          onClick={handleRebuildSearchIndex}
          disabled={isRebuildingIndex}
          style={{
            ...buttonStyle,
            background: '#eff6ff',
            color: '#1d4ed8',
            border: '1px solid #bfdbfe',
            opacity: isRebuildingIndex ? 0.65 : 1,
            cursor: isRebuildingIndex ? 'wait' : 'pointer'
          }}
          onMouseOver={(e) => { if (!isRebuildingIndex) e.target.style.background = '#dbeafe'; }}
          onMouseOut={(e) => { if (!isRebuildingIndex) e.target.style.background = '#eff6ff'; }}
        >
          {isRebuildingIndex ? '正在更新商品索引...' : '手動更新商品搜尋索引'}
        </button>
        {indexRebuildStatus && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: isRebuildingIndex ? '#1d4ed8' : '#475569' }}>
            {indexRebuildStatus}
          </div>
        )}
        <button 
          onClick={handleRebuildImageIndex}
          disabled={isRebuildingImageIndex}
          style={{
            ...buttonStyle,
            background: '#f5f3ff',
            color: '#6d28d9',
            border: '1px solid #ddd6fe',
            opacity: isRebuildingImageIndex ? 0.65 : 1,
            cursor: isRebuildingImageIndex ? 'wait' : 'pointer'
          }}
          onMouseOver={(e) => { if (!isRebuildingImageIndex) e.target.style.background = '#ede9fe'; }}
          onMouseOut={(e) => { if (!isRebuildingImageIndex) e.target.style.background = '#f5f3ff'; }}
        >
          {isRebuildingImageIndex ? '正在更新商品圖片索引...' : '手動更新商品圖片索引'}
        </button>
        {imageIndexRebuildStatus && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: isRebuildingImageIndex ? '#6d28d9' : '#475569' }}>
            {imageIndexRebuildStatus}
          </div>
        )}
        <button 
          onClick={handleMergeProducts}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          自動合併相同料號商品
        </button>
        <button 
          onClick={handleMergeProductsByBarcode}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          自動合併相同條碼商品
        </button>
        <button 
          onClick={handleListSameItemNumberProducts}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          手動合併相同料號商品
        </button>
        <button 
          onClick={handleMergeSameStyles}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          合併相同樣式
        </button>
        <button 
          onClick={handleCheckAbnormalProducts}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          異常商品檢查（料號不在品名中）
        </button>
        <button 
          onClick={() => setShowRemoveStyles(true)}
          style={{ ...buttonStyle, background: '#f5f5f5', color: '#333', border: '1px solid #e5e5e5' }}
          onMouseOver={(e) => { e.target.style.background = '#eee'; }}
          onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
        >
          移除商品樣式
        </button>

        {showMergeSelection && (
          <div style={{ marginTop: '1.5rem', padding: '1.5rem', border: '1px solid #e5e5e5', borderRadius: '12px', background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>選擇要合併的商品（相同料號）</h3>
            <div>
              <button 
                onClick={handleManualMergePreview} 
                disabled={Object.keys(selectedProducts).length < 2}
                style={{ 
                  marginRight: '10px',
                  padding: '8px 16px',
                  backgroundColor: Object.keys(selectedProducts).length >= 2 ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: Object.keys(selectedProducts).length >= 2 ? 'pointer' : 'not-allowed'
                }}
              >
                合併選中的商品 ({Object.keys(selectedProducts).length})
              </button>
              <button 
                onClick={() => {
                  setShowMergeSelection(false);
                  setSelectedProducts(new Set());
                }}
                style={{ 
                  padding: '8px 16px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                關閉
              </button>
            </div>
          </div>

          {sameItemNumberGroups.length === 0 ? (
            <p>沒有找到相同料號的商品</p>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {sameItemNumberGroups.map((group, groupIndex) => (
                <div key={groupIndex} style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <h4 style={{ marginTop: '0', color: '#2196F3' }}>料號: {group.itemNumber} (共 {group.products.length} 個商品)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '10px' }}>
                    {group.products.map((product) => (
                      <div 
                        key={product.id} 
                        onClick={() => toggleProductSelection(product.id)}
                        style={{
                          padding: '12px',
                          border: `2px solid ${selectedProducts[product.id] ? '#4CAF50' : '#ddd'}`,
                          borderRadius: '4px',
                          backgroundColor: selectedProducts[product.id] ? '#e8f5e9' : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <input
                            type="checkbox"
                            checked={!!selectedProducts[product.id]}
                            onChange={() => toggleProductSelection(product.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginRight: '8px', cursor: 'pointer' }}
                          />
                          <strong>{product.name || '無名稱'}</strong>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <div>ID: {product.id}</div>
                          <div>重量: {product.weight || '無'}</div>
                          <div>樣式數: {product.styles?.length || 0}</div>
                          <div>直播價: {product.livePrice || '無'}</div>
                          <div>商成價: {product.onlinePrice || '無'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

        {/* 合併預覽對話框 */}
        {mergePreview && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                合併預覽 ({mergePreview.type === 'auto' ? '自動合併' : '手動合併'})
              </h2>
              
              <div style={{ marginBottom: '1.5rem', fontSize: '14px', color: '#666' }}>
                共 {mergePreview.groups.length} 個料號組將被合併
              </div>

              <div style={{ maxHeight: '60vh', overflowY: 'auto', marginBottom: '1.5rem' }}>
                {mergePreview.groups.map((group, index) => (
                  <div key={index} style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    border: '1px solid #e5e5e5',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <h3 style={{ marginTop: 0, color: '#2196F3' }}>
                      料號: {group.itemNumber}
                    </h3>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>主商品：</strong>
                      <div style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                        <div>名稱: {group.mainProduct.name || '無名稱'}</div>
                        <div>ID: {group.mainProduct.id}</div>
                        <div>重量: {group.mainProduct.weight || '無'}</div>
                        <div>現有樣式: {group.currentStyles.length > 0 ? group.currentStyles.join(', ') : '無'}</div>
                        <div>現有條碼: {group.currentBarcodes.length > 0 ? group.currentBarcodes.join(', ') : '無'}</div>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <strong>將被合併的商品 ({group.mergeCandidates.length} 個)：</strong>
                      {group.mergeCandidates.map((candidate, idx) => (
                        <div key={idx} style={{ marginLeft: '1rem', marginTop: '0.5rem', fontSize: '14px' }}>
                          • {candidate.name || '無名稱'} (ID: {candidate.id})
                          {candidate.styles && candidate.styles.length > 0 && (
                            <span style={{ color: '#666' }}> - 樣式: {candidate.styles.join(', ')}</span>
                          )}
                        </div>
                      ))}
                    </div>

                    {group.newStyles.length > 0 && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                        <strong>新增的樣式：</strong>
                        <div style={{ marginTop: '0.5rem' }}>
                          {group.newStyles.map((style, idx) => (
                            <span key={idx} style={{
                              display: 'inline-block',
                              margin: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {style}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ padding: '0.75rem', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                      <strong>合併後的樣式 ({group.finalStyles.length} 個)：</strong>
                      <div style={{ marginTop: '0.5rem' }}>
                        {group.finalStyles.length > 0 ? (
                          group.finalStyles.map((style, idx) => (
                            <span key={idx} style={{
                              display: 'inline-block',
                              margin: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#ffc107',
                              color: '#000',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {style}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: '#666' }}>無</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button
                  onClick={() => setMergePreview(null)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmMerge}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  確認合併
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 異常商品檢查結果 */}
        {abnormalProducts && abnormalProducts.show && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>
                  異常商品檢查結果
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {abnormalProducts.abnormalCount > 0 && (
                    <button
                      onClick={handleExportAbnormalProducts}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      匯出 Excel
                    </button>
                  )}
                  <button
                    onClick={() => setAbnormalProducts(null)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    關閉
                  </button>
                </div>
              </div>
              
              <div style={{ marginBottom: '1.5rem', fontSize: '16px' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  總商品數：<strong>{abnormalProducts.total}</strong>
                </div>
                <div style={{ color: '#f44336', fontWeight: 'bold' }}>
                  異常商品數：<strong>{abnormalProducts.abnormalCount}</strong>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '14px', color: '#666' }}>
                  異常商品是指料號沒有出現在品名中的商品
                </div>
              </div>

              {abnormalProducts.abnormalCount === 0 ? (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  backgroundColor: '#e8f5e9',
                  borderRadius: '8px',
                  color: '#2e7d32',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  ✓ 沒有發現異常商品！
                </div>
              ) : (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #ddd' }}>商品名稱</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #ddd' }}>料號</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #ddd' }}>商品ID</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #ddd' }}>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abnormalProducts.products.map((product, index) => (
                        <tr key={product.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                            {product.name || '無名稱'}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #ddd', fontWeight: 'bold', color: '#f44336' }}>
                            {product.itemNumber}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #ddd', fontSize: '12px', color: '#666' }}>
                            {product.id}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                            <button
                              onClick={() => {
                                window.open(`/view/${country}/${product.id}`, '_blank');
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              查看
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 移除商品樣式界面 */}
        {showRemoveStyles && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              minWidth: '600px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>移除商品樣式</h2>
                <button
                  onClick={() => {
                    setShowRemoveStyles(false);
                    setItemNumbersInput('');
                    setBarcodeKeyword('');
                    setProductsWithStyles([]);
                    setSelectedStylesToRemove({});
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  關閉
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  輸入料號（一行一個）：
                </label>
                <textarea
                  value={itemNumbersInput}
                  onChange={(e) => setItemNumbersInput(e.target.value)}
                  placeholder="例如：&#10;KA1001&#10;KA1002&#10;KA1003"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
                <button
                  onClick={handleSearchProductsByItemNumbers}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  查詢商品樣式
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  或輸入條碼關鍵字（自動選中符合的樣式）：
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={barcodeKeyword}
                    onChange={(e) => setBarcodeKeyword(e.target.value)}
                    placeholder="例如：8801234"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchStylesByBarcode();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={handleSearchStylesByBarcode}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    搜尋條碼關鍵字
                  </button>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '12px', color: '#666' }}>
                  提示：會在所有商品中搜尋包含此關鍵字的條碼，並自動選中符合的樣式
                </div>
              </div>

              {/* 批量操作按鈕區域 - 始終顯示 */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f0f0', borderRadius: '8px', border: '1px solid #ddd' }}>
                <div style={{ marginBottom: '0.75rem', fontWeight: 'bold', fontSize: '16px' }}>
                  批量操作
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleRemoveEmptyBarcodeStyles}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    移除所有空白條碼樣式
                  </button>
                  <button
                    onClick={handleRemoveEmptyBarcodeProducts}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#e91e63',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    移除空白條碼商品（排除組合商品）
                  </button>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '12px', color: '#666' }}>
                  提示：這些操作會掃描所有商品，無需先查詢
                </div>
              </div>

              {productsWithStyles.length > 0 && (
                <>
                  <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>找到 {productsWithStyles.length} 個商品</strong>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      已選擇 <strong>{Object.keys(selectedStylesToRemove).length}</strong> 個樣式要移除
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={handleRemoveSelectedStyles}
                        disabled={Object.keys(selectedStylesToRemove).length === 0}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: Object.keys(selectedStylesToRemove).length > 0 ? '#4CAF50' : '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: Object.keys(selectedStylesToRemove).length > 0 ? 'pointer' : 'not-allowed',
                          fontSize: '14px'
                        }}
                      >
                        移除選中的樣式 ({Object.keys(selectedStylesToRemove).length})
                      </button>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px' }}>全部移除驗證碼：</span>
                        <strong style={{ fontSize: '18px', color: '#f44336' }}>{removeAllVerificationCode}</strong>
                        <input
                          type="text"
                          value={removeAllInputCode}
                          onChange={(e) => setRemoveAllInputCode(e.target.value)}
                          placeholder="輸入驗證碼"
                          maxLength={4}
                          style={{
                            width: '100px',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '16px',
                            textAlign: 'center'
                          }}
                        />
                        <button
                          onClick={handleRemoveAllStyles}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          全部移除
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    {productsWithStyles.map((product) => (
                      <div
                        key={product.id}
                        style={{
                          marginBottom: '1rem',
                          padding: '1rem',
                          border: '1px solid #e5e5e5',
                          borderRadius: '8px',
                          backgroundColor: '#fafafa'
                        }}
                      >
                        <div style={{ marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '16px' }}>
                          {product.name} (料號: {product.itemNumber})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {product.styles.map((style, index) => {
                            const key = `${product.id}-${index}`;
                            const isSelected = !!selectedStylesToRemove[key];
                            return (
                              <div
                                key={index}
                                onClick={() => toggleStyleSelection(product.id, index)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: isSelected ? '#f44336' : '#e0e0e0',
                                  color: isSelected ? 'white' : '#333',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  transition: 'all 0.2s',
                                  textDecoration: isSelected ? 'line-through' : 'none'
                                }}
                              >
                                <span>{isSelected ? '✕' : '○'}</span>
                                <span>{style || '無樣式'}</span>
                                {product.rawStyleBarcodes && product.rawStyleBarcodes[index] && (
                                  <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                    ({product.rawStyleBarcodes[index]})
                                  </span>
                                )}
                                {/* 顯示樣式圖片：只有當樣式有專屬圖片時才顯示 */}
                                {product.styleDetails?.[index]?.image && (
                                  <img
                                    src={product.styleDetails[index].image}
                                    alt={style}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      objectFit: 'cover',
                                      borderRadius: '2px',
                                      marginLeft: '0.25rem',
                                      verticalAlign: 'middle'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BatchAddProduct;
